/**
 * The auction reducer.
 *
 *     reduce(state, command) -> Decision
 *
 * Pure. No I/O, no clock, no randomness, no mutation. Time arrives on the
 * command as `now`; sequence numbers come from state; event IDs are stamped
 * later by the engine.
 *
 * That purity is not a style preference. It is what makes replay deterministic,
 * lets the same code run in the browser so a bidder's screen is provably
 * consistent with server truth, and allows the race conditions in spec section
 * 10 to be tested exhaustively without infrastructure.
 *
 * ADR-42 - ONE BID CORE, MODE ORCHESTRATION ON TOP. Every accepted bid, whatever
 * its source (ONLINE, FLOOR, PROXY) and whatever the auction mode, goes through
 * `acceptBidIntoPosition`: one place computes the new high, the next ask from
 * the increment table, the undo record, and the BID_ACCEPTED event. Proxy (max)
 * bids are resolved INSIDE the reducer by `settleProxies` - deterministic, one
 * increment at a time, in the same Decision as the command that made them
 * eligible - never by a side worker that could race the ring. Mode decides how a
 * lot progresses and closes: LIVE is the clerk lifecycle; TIMED is per-lot
 * positions with an AUTHORITATIVE scheduled close swept by TICK (and therefore by
 * `step()` before every command), soft-close extension, and automatic disposition
 * to the same canonical LOT_SOLD.
 */

import { getIncrementTable, nextAsk, nextAskByStep, stepFor } from './increments.js';
import { CONTROL_PLANE_ACTOR_ID, MAX_BID_VIEW_SCOPES, isIntentControls } from './permissions.js';
import { computeExtension } from './timed.js';
import { SALE_WIDE_SCOPE, STALE_GUARDED_COMMANDS, isCloseStep } from './types.js';
import type {
  BidderAuthorization,
  BidSource,
  Command,
  Decision,
  DepartedLot,
  DomainEvent,
  EqualBidAward,
  ExpectedRingView,
  LotPosition,
  LotState,
  MaxBid,
  MaxBidViewerRole,
  PendingBid,
  PendingResolution,
  PreviousLot,
  RejectionReason,
  RequiredAcknowledgement,
  RingSale,
  RingState,
  SellingGroup,
  SoldGroup,
  StaleGuardedCommandType,
  TimedLot,
  TimedLotPhase,
  ViewedMaxBid,
} from './types.js';

/** Spec 9.1: if no authorized floor-input client has heartbeated within this
 *  window during an open lot, online bidding auto-pauses. */
export const FLOOR_INPUT_TIMEOUT_MS = 1_500;

const BIDDING_STATES: readonly LotState[] = ['OPEN', 'FAIR_WARNING'];
/** States in which HAMMER has already committed. A bid arriving now is late,
 *  and the distinction from LOT_NOT_OPEN matters: POST_HAMMER is the evidence
 *  that answers "I pressed bid before it sold". */
const POST_HAMMER_STATES: readonly LotState[] = ['HAMMERED_PENDING', 'SOLD', 'NO_SALE'];
const TIMED_BIDDING_PHASES: readonly TimedLotPhase[] = ['OPEN', 'EXTENDED'];

/** Hard bound on proxy-resolution steps per decision. Unreachable in practice
 *  (the ask strictly increases each step and maxes are finite); it exists so a
 *  pathological increment table can never spin the reducer. */
const PROXY_STEP_GUARD = 100_000;

/**
 * Max proxy events committed in ONE decision. A durable commit is a single
 * conditional transaction (DynamoDB TransactWriteItems caps at 100 items: STATE
 * + events + outcome + attempt + changed max/bidder items). A wide gap on a fine
 * ladder can alternate hundreds of steps; capping here keeps every commit well
 * under the ceiling. When the cap is hit the alternation is NOT abandoned - the
 * engine calls back with CONTINUE_PROXY_RESOLUTION until `pendingProxyLots` is
 * empty, so the same real ladder-aligned bids land, just across bounded commits.
 */
const PROXY_STEPS_PER_DECISION = 40;

/** Time-driven transitions are the engine's act, not any human's. Public
 *  redaction strips actorId anyway; the audit log names the engine. */
const ENGINE_ACTOR = 'engine:timed-close';

/** The LIVE clerk lifecycle has no meaning on a TIMED ring (and vice versa). A
 *  hybrid where a clerk could OPEN_LOT on a timed ring would be two lifecycles
 *  fighting over one state - refused outright. */
const LIVE_ONLY_COMMANDS: ReadonlySet<Command['type']> = new Set([
  'SET_CURRENT_LOT', 'OPEN_LOT', 'SET_ASK', 'DECLARE_FAIR_WARNING', 'HAMMER', 'DECLARE_PASSED', 'REOPEN_LOT', 'CORRECT_FLOOR_BID', 'PLACE_FLOOR_BID',
  'ACCEPT_ONLINE_BID', 'DECLINE_ONLINE_BID', 'SET_CONFIRM_MODE', 'AWARD_EQUAL_BID_ONSITE', 'RESTORE_EQUAL_BID_ONLINE',
  // ADR-70 cut 3: the auctioneer's close and the ring's advance are LIVE acts.
  'SET_CLOSE_STEP', 'SELL_AND_ADVANCE', 'PASS_AND_ADVANCE',
  // ADR-70 cut 6c: taking a bid back and resetting the board are the ring's.
  'RETRACT_LAST_BID', 'RESET_BIDDING',
  // ADR-70 cut 6d: the lot's increment and taking it out of the ring.
  'SET_INCREMENT', 'REMOVE_FROM_RING',
]);

// ---------------------------------------------------------------------------

function reject(state: RingState, reason: RejectionReason): Decision {
  return { status: 'rejected', reason, state };
}

function accept(state: RingState, events: readonly DomainEvent[]): Decision {
  return { status: 'accepted', state, events };
}

// ---------------------------------------------------------------------------
// ADR-70: the operator's word lands on the state they saw
// ---------------------------------------------------------------------------

const STALE_GUARDED: ReadonlySet<Command['type']> = new Set(STALE_GUARDED_COMMANDS);

type StaleGuarded = Extract<Command, { type: StaleGuardedCommandType }>;

const isStaleGuarded = (command: Command): command is StaleGuarded => STALE_GUARDED.has(command.type);

/** The command acts on the lot it names (every guarded command but
 *  SET_CURRENT_LOT, whose lot id is the destination, and REOPEN_LOT, which
 *  since cut 6c may name the lot that just left; its own case checks the
 *  lot, and `expected` is still judged against the ring). */
const namesLotInRing = (command: StaleGuarded): boolean => command.type !== 'SET_CURRENT_LOT' && command.type !== 'REOPEN_LOT';

const isSeqOrNull = (v: unknown): boolean => v === null || (typeof v === 'number' && Number.isInteger(v) && v >= 0);

/** The wire is untyped (FLOOR_COMMAND spreads a JSON record), so the shape is
 *  checked here before a single field is trusted. */
function isExpectedView(v: unknown): v is ExpectedRingView {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return false;
  const e = v as Record<string, unknown>;
  if (!(e['lotId'] === null || typeof e['lotId'] === 'string')) return false;
  if (!isSeqOrNull(e['highBidSeq'])) return false;
  if (!(e['pendingBidId'] === undefined || e['pendingBidId'] === null || typeof e['pendingBidId'] === 'string')) return false;
  if (!(e['ask'] === undefined || e['ask'] === null || (typeof e['ask'] === 'number' && Number.isFinite(e['ask'])))) return false;
  return true;
}

/**
 * LIVE clerk commands that settle or move the lot in the ring are judged
 * against the ring, never against the client's idea of it:
 *
 * - a command naming a lot must name the lot in the ring (an old console
 *   that never sends `expected` is held to this too), and
 * - a command carrying `expected` must match the ring exactly: same lot,
 *   same standing high bid, and, when sent, the same held internet bid and
 *   the same ask.
 *
 * Otherwise STALE_AUCTION_STATE and nothing changes. Bids are already pinned
 * by `atAsk`, VOID_SALE by `soldSeq`, CORRECT_FLOOR_BID by `reversesSeq`,
 * ACCEPT / DECLINE by `pendingBidId`, OPEN_LOT by the current lot; this is the
 * same rule for the commands that had none.
 */
function staleView(state: RingState, command: StaleGuarded): Decision | null {
  if (namesLotInRing(command) && command.lotId !== state.currentLotId) return reject(state, 'STALE_AUCTION_STATE');
  const expected: unknown = command.expected;
  if (expected === undefined) return null;
  if (!isExpectedView(expected)) return reject(state, 'STALE_AUCTION_STATE');
  if (expected.lotId !== state.currentLotId) return reject(state, 'STALE_AUCTION_STATE');
  if (expected.highBidSeq !== state.currentHighBidSeq) return reject(state, 'STALE_AUCTION_STATE');
  if (expected.pendingBidId !== undefined && expected.pendingBidId !== (state.pendingBid?.id ?? null)) {
    return reject(state, 'STALE_AUCTION_STATE');
  }
  if (expected.ask !== undefined && expected.ask !== state.currentAsk) return reject(state, 'STALE_AUCTION_STATE');
  return null;
}

const bumpSeq = (n: number): Pick<RingState, 'lastSeq' | 'nextSeq'> => ({ lastSeq: n, nextSeq: n + 1 });

/** Anonymized public reference for a bidder (spec 8.2). Deterministic so the
 *  same bidder reads the same all sale long. Real identity and paddle number
 *  travel on the floor channel only, never in a public event. */
function bidderRef(bidderId: string, source: BidSource): string {
  if (source === 'FLOOR') return 'Floor';
  let hash = 0;
  for (let i = 0; i < bidderId.length; i++) hash = (hash * 31 + bidderId.charCodeAt(i)) % 1000;
  return `Online Bidder ${hash.toString().padStart(3, '0')}`;
}

function isFloorInput(role: string): boolean {
  return role === 'clerk' || role === 'spotter';
}

/**
 * Evaluate the floor-input auto-pause rule.
 *
 * Folded into every command rather than living only on TICK, so that a bid
 * arriving during a floor-input blackout cannot slip in ahead of the pause. The
 * rule is enforced at the moment of decision, not on a timer's schedule.
 */
function autoPause(state: RingState, now: number, seq: number, actorId: string): DomainEvent | null {
  if (state.onlineStatus !== 'OPEN') return null;
  if (!BIDDING_STATES.includes(state.lotState)) return null;
  const last = state.floorInputHealthyAt;
  if (last !== null && now - last < FLOOR_INPUT_TIMEOUT_MS) return null;
  return {
    type: 'ONLINE_PAUSED',
    reason: 'FLOOR_INPUT_LOST',
    seq,
    channel: 'public',
    actorId,
    serverTs: now,
  };
}

// ---------------------------------------------------------------------------
// The shared bid core (ADR-42)
// ---------------------------------------------------------------------------

interface BidInput {
  readonly lotId: string;
  readonly bidderId: string;
  readonly amount: number;
  readonly source: BidSource;
  readonly paddleNumber: string | null;
  readonly seq: number;
  readonly now: number;
  readonly lotState: LotState;
  /** For PROXY bids: the MAX_BID_PLACED seq this executes on behalf of. */
  readonly proxyOfMaxSeq?: number;
}

/**
 * Accept a bid into a bidding position. THE one place a bid becomes state:
 * new high, next ask from the increment table, the undo record, and the
 * BID_ACCEPTED event. Callers have already validated authorization, the at-ask
 * guard, and lot state - this only commits the arithmetic, identically for a
 * live ring position, a timed lot, or a proxy execution.
 */
function acceptBidIntoPosition(
  pos: LotPosition,
  incrementTableId: string,
  b: BidInput,
  chosenStep: number | null = null,
): { readonly pos: LotPosition; readonly event: DomainEvent } {
  // ADR-70 cut 6d: the clerk's chosen increment for the lot in the ring, else
  // the sale's table. Still the one place the next ask is computed.
  const advanced = chosenStep !== null ? nextAskByStep(b.amount, chosenStep) : nextAsk(b.amount, getIncrementTable(incrementTableId));
  const event: DomainEvent = {
    type: 'BID_ACCEPTED',
    lotId: b.lotId,
    bidAmount: b.amount,
    bidSource: b.source,
    bidderRef: bidderRef(b.bidderId, b.source),
    bidderId: b.bidderId,
    paddleNumber: b.paddleNumber,
    previousHighBid: pos.currentHighBid,
    newHighBid: b.amount,
    nextAsk: advanced,
    // ADR-70 cut 3 (Q3): a bid during the close takes the lot back to open
    // bidding. The event says so, so a client following events reads "open"
    // exactly as the ring does (withRingPosition commits the same).
    lotState: b.lotState === 'FAIR_WARNING' ? 'OPEN' : b.lotState,
    ...(b.proxyOfMaxSeq !== undefined ? { proxyOfMaxSeq: b.proxyOfMaxSeq } : {}),
    seq: b.seq,
    channel: 'public',
    actorId: b.bidderId,
    serverTs: b.now,
  };
  return {
    event,
    pos: {
      currentHighBid: b.amount,
      currentHighBidder: b.bidderId,
      currentHighBidSource: b.source,
      currentHighPaddle: b.paddleNumber,
      currentHighBidSeq: b.seq,
      currentAsk: advanced,
      lastBid: {
        seq: b.seq,
        source: b.source,
        bidderId: b.bidderId,
        amount: b.amount,
        previousHighBid: pos.currentHighBid,
        previousHighBidder: pos.currentHighBidder,
        previousHighBidSource: pos.currentHighBidSource,
        previousHighPaddle: pos.currentHighPaddle,
        previousHighBidSeq: pos.currentHighBidSeq,
        previousAsk: pos.currentAsk,
      },
    },
  };
}

/** ADR-70 cut 6d: the step the clerk chose for the lot in the ring, or null
 *  (the sale's table). A choice applies only to the lot it was made for. */
function chosenStepOf(s: RingState): number | null {
  const c = s.chosenIncrement;
  return c !== null && c.lotId === s.currentLotId ? c.step : null;
}

/** ADR-70 cut 6d: the increment the ring bids up by at an ask of `at`: the
 *  chosen step, else the table's step there. Exported for the floor snapshot,
 *  so the console shows the ring's figure rather than its own copy of a table. */
export function effectiveIncrement(s: RingState, at: number): number {
  return chosenStepOf(s) ?? stepFor(at, getIncrementTable(s.incrementTableId));
}

/** The LIVE ring's single bidding position, as a LotPosition. */
function ringPosition(s: RingState): LotPosition {
  return {
    currentHighBid: s.currentHighBid,
    currentHighBidder: s.currentHighBidder,
    currentHighBidSource: s.currentHighBidSource,
    currentHighPaddle: s.currentHighPaddle,
    currentHighBidSeq: s.currentHighBidSeq,
    currentAsk: s.currentAsk,
    lastBid: s.lastBid,
  };
}

/**
 * Commit a new bidding position to the LIVE ring. Every writer is a bid
 * commit, so this is also where ADR-70 cut 3's rule lives (Q3): an accepted
 * bid during the close ("going once" with a hand up) returns the lot to OPEN
 * and clears the close step. The auctioneer calls the close again from the
 * top, exactly as in the room.
 */
function withRingPosition(s: RingState, p: LotPosition): RingState {
  const reopened = s.lotState === 'FAIR_WARNING' ? { lotState: 'OPEN' as const, closeStep: null } : {};
  return { ...s, ...p, ...reopened };
}

function timedPosition(l: TimedLot): LotPosition {
  return {
    currentHighBid: l.currentHighBid,
    currentHighBidder: l.currentHighBidder,
    currentHighBidSource: l.currentHighBidSource,
    currentHighPaddle: l.currentHighPaddle,
    currentHighBidSeq: l.currentHighBidSeq,
    currentAsk: l.currentAsk,
    lastBid: l.lastBid,
  };
}

function withLot(s: RingState, lot: TimedLot): RingState {
  return { ...s, lots: { ...s.lots, [lot.lotId]: lot } };
}

function setMax(maxBids: RingState['maxBids'], lotId: string, bidderId: string, m: MaxBid): RingState['maxBids'] {
  return { ...maxBids, [lotId]: { ...(maxBids[lotId] ?? {}), [bidderId]: m } };
}

/** A bid on a timed lot is legal only while the lot is OPEN/EXTENDED AND
 *  engine `now` is before its authoritative scheduled close - whether or not a
 *  TICK has materialized the close yet. */
function timedLotBiddable(lot: TimedLot | undefined, now: number): lot is TimedLot {
  return !!lot && TIMED_BIDDING_PHASES.includes(lot.phase) && now < lot.scheduledCloseAt;
}

// ---------------------------------------------------------------------------
// TIMED orchestration (ADR-42 §4)
// ---------------------------------------------------------------------------

/**
 * Soft-close extension for `lotId` after the accepted bid `triggeringBidSeq`
 * at `now`, plus cascade to its close group. Every move is a LOT_CLOSE_EXTENDED
 * event carrying previous/new close, the triggering bid, the extension number,
 * and the policy that produced it - the history is provable from the log.
 */
function applyExtension(state: RingState, lotId: string, triggeringBidSeq: number, now: number): { readonly state: RingState; readonly events: DomainEvent[] } {
  const events: DomainEvent[] = [];
  let s = state;
  const lot = s.lots[lotId];
  if (!lot) return { state: s, events };
  const ext = computeExtension(lot, now, s.policy.timed);
  if (!ext) return { state: s, events };
  const policy = { extensionWindowMs: s.policy.timed.extensionWindowMs, extensionDurationMs: s.policy.timed.extensionDurationMs, extendFrom: s.policy.timed.extendFrom };

  const extendOne = (target: TimedLot, cascadedFromLotId: string | null): void => {
    const seq = s.nextSeq;
    events.push({
      type: 'LOT_CLOSE_EXTENDED',
      lotId: target.lotId,
      previousCloseAt: target.scheduledCloseAt,
      newCloseAt: ext.newCloseAt,
      triggeringBidSeq,
      extensionNumber: target.extensions + 1,
      policy,
      cascadedFromLotId,
      seq,
      channel: 'public',
      actorId: ENGINE_ACTOR,
      serverTs: now,
    });
    s = withLot({ ...s, ...bumpSeq(seq) }, { ...target, scheduledCloseAt: ext.newCloseAt, extensions: target.extensions + 1, phase: 'EXTENDED' });
  };

  extendOne(lot, null);
  // Cascade: siblings in the same close group move to the same new close (only
  // if that is later than where they stand). Deterministic order by lotId.
  if (lot.closeGroupId !== null) {
    const siblings = Object.values(s.lots)
      .filter((l) => l.lotId !== lotId && l.closeGroupId === lot.closeGroupId && TIMED_BIDDING_PHASES.includes(l.phase) && l.scheduledCloseAt < ext.newCloseAt)
      .sort((a, b) => (a.lotId < b.lotId ? -1 : a.lotId > b.lotId ? 1 : 0));
    for (const sib of siblings) extendOne(sib, lotId);
  }
  return { state: s, events };
}

/**
 * ADR-65: remember a sale on the ring. One entry per lot the LOT_SOLD names,
 * all at the one soldSeq (a group sells on one bid); a resale after a void
 * replaces the entry, its first life staying in the log and the ledger. This
 * is what the LOT_ALREADY_SOLD guard and VOID_SALE read.
 */
function withSales(sales: RingState['sales'], lotIds: readonly string[], sale: Omit<RingSale, 'groupLotIds' | 'voidedSeq'>): RingState['sales'] {
  const next: Record<string, RingSale> = { ...sales };
  const groupLotIds = lotIds.length > 1 ? [...lotIds] : null;
  for (const lotId of lotIds) next[lotId] = { ...sale, groupLotIds, voidedSeq: null };
  return next;
}

/** ADR-65: true if any of these lots has a sale that still stands (R2). */
function standingSale(state: RingState, lotIds: readonly string[]): boolean {
  return lotIds.some((lotId) => {
    const s = state.sales[lotId];
    return s !== undefined && s.voidedSeq === null;
  });
}

/** Dispose a CLOSED_PENDING timed lot: the canonical LOT_SOLD (or LOT_NO_SALE). */
function disposeTimedLot(state: RingState, lot: TimedLot, now: number, actorId: string, noSaleCause: 'NO_BIDS' | 'RESERVE_NOT_MET' | 'CLERK' | null): { readonly state: RingState; readonly events: DomainEvent[] } {
  const seq = state.nextSeq;
  const sold = lot.currentHighBid !== null && lot.currentHighBidder !== null && noSaleCause === null;
  if (sold) {
    const ev: DomainEvent = {
      type: 'LOT_SOLD',
      lotId: lot.lotId,
      price: lot.currentHighBid!,
      buyerId: lot.currentHighBidder!,
      bidSource: lot.currentHighBidSource ?? 'ONLINE',
      pricingMode: lot.pricingMode,
      winningPaddleNumber: lot.currentHighPaddle,
      winningBidSeq: lot.currentHighBidSeq,
      dispositionCause: 'TIMED_CLOSE',
      seq,
      channel: 'public',
      actorId,
      serverTs: now,
    };
    // ADR-64: a timed close is a disposition too; the ring's "previous lot"
    // is its most recent one, whichever path committed it.
    const previousLot: PreviousLot = { lotId: lot.lotId, outcome: 'SOLD', price: lot.currentHighBid!, seq, at: now };
    // ADR-65: the ring remembers what it sold, timed path included.
    const sales = withSales(state.sales, [lot.lotId], { soldSeq: seq, price: lot.currentHighBid!, bidSource: lot.currentHighBidSource ?? 'ONLINE', buyerId: lot.currentHighBidder!, paddle: lot.currentHighPaddle });
    return { state: withLot({ ...state, previousLot, sales, requiredAcks: withoutAckScope(state.requiredAcks, lot.lotId), ...bumpSeq(seq) }, { ...lot, phase: 'SOLD' }), events: [ev] };
  }
  const cause = noSaleCause ?? (lot.currentHighBid === null ? 'NO_BIDS' : 'RESERVE_NOT_MET');
  const ev: DomainEvent = { type: 'LOT_NO_SALE', lotId: lot.lotId, cause, seq, channel: 'public', actorId, serverTs: now };
  const previousLot: PreviousLot = { lotId: lot.lotId, outcome: 'NO_SALE', price: lot.currentHighBid, seq, at: now };
  return { state: withLot({ ...state, previousLot, ...bumpSeq(seq) }, { ...lot, phase: 'NO_SALE' }), events: [ev] };
}

/**
 * The authoritative close sweep. Every OPEN/EXTENDED timed lot whose
 * `scheduledCloseAt <= now` closes - in deterministic order (close time, then
 * lotId) - emitting TIMED_LOT_CLOSED, then (policy.autoDisposition) LOT_SOLD /
 * LOT_NO_SALE in the same tick. Idempotent: a closed lot is not reclosed; a
 * TICK whose `now` is earlier than a close does nothing; a late TICK closes
 * everything overdue with the same result it would have had on time. Timer
 * DELIVERY is never the source of truth - the committed scheduledCloseAt is.
 */
function sweepTimedCloses(state: RingState, now: number): { readonly state: RingState; readonly events: DomainEvent[] } {
  const events: DomainEvent[] = [];
  let s = state;
  if (s.policy.mode !== 'TIMED') return { state: s, events };
  const due = Object.values(s.lots)
    .filter((l) => TIMED_BIDDING_PHASES.includes(l.phase) && l.scheduledCloseAt <= now)
    .sort((a, b) => a.scheduledCloseAt - b.scheduledCloseAt || (a.lotId < b.lotId ? -1 : a.lotId > b.lotId ? 1 : 0));
  for (const lot of due) {
    const seq = s.nextSeq;
    events.push({
      type: 'TIMED_LOT_CLOSED',
      lotId: lot.lotId,
      scheduledCloseAt: lot.scheduledCloseAt,
      extensions: lot.extensions,
      finalHighBid: lot.currentHighBid,
      finalBidderRef: lot.currentHighBidder !== null ? bidderRef(lot.currentHighBidder, lot.currentHighBidSource ?? 'ONLINE') : null,
      finalBidSource: lot.currentHighBidSource,
      seq,
      channel: 'public',
      actorId: ENGINE_ACTOR,
      serverTs: now,
    });
    const closed: TimedLot = { ...lot, phase: 'CLOSED_PENDING', closeSeq: seq };
    s = withLot({ ...s, ...bumpSeq(seq) }, closed);
    if (s.policy.timed.autoDisposition) {
      const belowReserve = closed.reserve !== null && closed.currentHighBid !== null && closed.currentHighBid < closed.reserve;
      const d = disposeTimedLot(s, closed, now, ENGINE_ACTOR, closed.currentHighBid === null ? 'NO_BIDS' : belowReserve ? 'RESERVE_NOT_MET' : null);
      s = d.state;
      events.push(...d.events);
    }
  }
  return { state: s, events };
}

// ---------------------------------------------------------------------------
// Proxy resolution - one loop for every position (ADR-42 §2.2)
// ---------------------------------------------------------------------------

/** How the settle loop reads/writes a position, per mode. */
interface PositionAccess {
  /** The position if it is biddable right now, else null (stop). */
  get(s: RingState, now: number): LotPosition | null;
  set(s: RingState, p: LotPosition): RingState;
  /** The LotState stamped on BID_ACCEPTED. */
  lotState(s: RingState): LotState;
  /** Mode hook after an accepted proxy bid (TIMED: soft-close extension). */
  afterBid(s: RingState, bidSeq: number, now: number): { readonly state: RingState; readonly events: DomainEvent[] };
  /** ADR-70 cut 6d: the clerk's chosen step (LIVE), or null (the table). */
  chosenStep(s: RingState): number | null;
}

const liveAccess = (lotId: string): PositionAccess => ({
  get: (s) => (s.currentLotId === lotId && BIDDING_STATES.includes(s.lotState) ? ringPosition(s) : null),
  set: (s, p) => withRingPosition(s, p),
  lotState: (s) => s.lotState,
  afterBid: (s) => ({ state: s, events: [] }),
  chosenStep: chosenStepOf,
});

const timedAccess = (lotId: string): PositionAccess => ({
  get: (s, now) => {
    const lot = s.lots[lotId];
    return timedLotBiddable(lot, now) ? timedPosition(lot) : null;
  },
  set: (s, p) => {
    const lot = s.lots[lotId];
    return lot ? withLot(s, { ...lot, ...p }) : s;
  },
  lotState: () => 'OPEN',
  afterBid: (s, bidSeq, now) => applyExtension(s, lotId, bidSeq, now),
  chosenStep: () => null,
});

const accessFor = (s: RingState, lotId: string): PositionAccess => (s.policy.mode === 'TIMED' ? timedAccess(lotId) : liveAccess(lotId));

/**
 * Runs after any accepted transition that can make a max eligible. Each step:
 * pick the eligible max with the highest amount (earlier `placedSeq` breaks a
 * tie - identical maxes: the earlier one wins AT that max), and place a real
 * bid AT THE ASK for that bidder. Repeat until nobody eligible remains. Every
 * step is a complete, ladder-aligned BID_ACCEPTED (bidSource PROXY) through the
 * shared bid core, so "high <= ask" and "accepted bid == ask" hold unchanged and
 * the log reads exactly like the human back-and-forth. Maxes that can no
 * longer meet the ask (or whose limit/approval blocks them) emit PROXY_EXHAUSTED
 * once and stop.
 *
 * Proxies are online bids: they do not execute while online is PAUSED (they
 * run on RESUME_ONLINE), and only while the lot is biddable per its mode (live:
 * OPEN/FAIR_WARNING in the ring; timed: OPEN/EXTENDED and before the close).
 */
function settleProxies(state: RingState, lotId: string, now: number): { readonly state: RingState; readonly events: DomainEvent[] } {
  const events: DomainEvent[] = [];
  let s = state;
  if (!s.policy.proxy.enabled) return { state: s, events };
  if (s.onlineStatus === 'PAUSED') return { state: s, events };
  const access = accessFor(s, lotId);

  for (let guard = 0; guard < PROXY_STEP_GUARD; guard++) {
    // Commit-size safety: stop after PROXY_STEPS_PER_DECISION events; the engine
    // finishes the rest with CONTINUE_PROXY_RESOLUTION, one bounded commit each.
    if (events.length >= PROXY_STEPS_PER_DECISION) break;
    const maxes = s.maxBids[lotId];
    if (!maxes) break;
    const pos = access.get(s, now);
    if (!pos) break;
    const ask = pos.currentAsk;
    if (ask === null) break;

    const eligible: MaxBid[] = [];
    for (const m of Object.values(maxes)) {
      if (m.status !== 'ACTIVE') continue;
      if (m.bidderId === pos.currentHighBidder) continue; // never outbids itself
      const auth = s.bidders[m.bidderId];
      let reason: 'MAX_REACHED' | 'BID_LIMIT' | 'NOT_APPROVED' | null = null;
      if (!auth || auth.approvalStatus !== 'APPROVED') reason = 'NOT_APPROVED';
      else if (m.maxAmount < ask) reason = 'MAX_REACHED';
      else if (auth.bidLimit !== undefined && ask > auth.bidLimit) reason = 'BID_LIMIT';
      if (reason !== null) {
        const seq = s.nextSeq;
        events.push({ type: 'PROXY_EXHAUSTED', lotId, bidderId: m.bidderId, maxSeq: m.placedSeq, atAsk: ask, reason, seq, channel: 'private', actorId: m.bidderId, serverTs: now });
        s = { ...s, maxBids: setMax(s.maxBids, lotId, m.bidderId, { ...m, status: 'EXHAUSTED', endedReason: reason }), ...bumpSeq(seq) };
        continue;
      }
      eligible.push(m);
    }
    if (eligible.length === 0) break;

    eligible.sort((a, b) => b.maxAmount - a.maxAmount || a.placedSeq - b.placedSeq);
    const w = eligible[0]!;
    // ADR-60 confirm mode: a proxy step is an internet bid, so it waits for the
    // operator like any other - held as the ring's one pending bid, then the
    // loop stops. The next step (if any) pends after the operator answers.
    if (s.confirmMode && s.policy.mode !== 'TIMED') {
      if (s.pendingBid !== null) break;
      const held = holdPending(s, { bidderId: w.bidderId, sessionId: w.sessionId, lotId, amount: ask, bidSource: 'PROXY', proxyOfMaxSeq: w.placedSeq, idempotencyKey: null }, now, w.bidderId);
      s = held.state;
      events.push(...held.events);
      break;
    }
    const seq = s.nextSeq;
    const r = acceptBidIntoPosition(pos, s.incrementTableId, {
      lotId, bidderId: w.bidderId, amount: ask, source: 'PROXY', paddleNumber: null, seq, now, lotState: access.lotState(s), proxyOfMaxSeq: w.placedSeq,
    }, access.chosenStep(s));
    s = access.set({ ...s, ...bumpSeq(seq) }, r.pos);
    events.push(r.event);
    const after = access.afterBid(s, seq, now);
    s = after.state;
    events.push(...after.events);
  }
  return { state: s, events };
}

/**
 * Lots that still have proxy work to do - an ACTIVE max not currently holding
 * the high bid, on a biddable position. The engine polls this after a commit
 * to know whether a capped alternation needs another CONTINUE_PROXY_RESOLUTION
 * commit. Pure; empty when proxy resolution is quiescent. Matches settleProxies'
 * termination condition exactly, so the loop cannot spin.
 */
export function pendingProxyLots(state: RingState, now: number): string[] {
  if (!state.policy.proxy.enabled || state.onlineStatus === 'PAUSED') return [];
  // Confirm mode: the pending bid is the proxy work in progress; nothing more
  // to do until the operator answers (mirrors settleProxies' early stop).
  if (state.confirmMode && state.policy.mode !== 'TIMED' && state.pendingBid !== null) return [];
  const out: string[] = [];
  for (const lotId of Object.keys(state.maxBids)) {
    const pos = accessFor(state, lotId).get(state, now);
    if (!pos) continue;
    for (const m of Object.values(state.maxBids[lotId] ?? {})) {
      if (m.status === 'ACTIVE' && m.bidderId !== pos.currentHighBidder) {
        out.push(lotId);
        break;
      }
    }
  }
  return out;
}

/** Invalidate every ACTIVE max matching `pick` - one MAX_BID_INVALIDATED each.
 *  Never silent: the log always says which maxes stopped and why. */
function invalidateMaxes(
  state: RingState,
  pick: (m: MaxBid) => boolean,
  reason: string,
  reconfirmRequested: boolean,
  now: number,
  actorId: string,
): { readonly state: RingState; readonly events: DomainEvent[] } {
  const events: DomainEvent[] = [];
  let s = state;
  for (const [lotId, byBidder] of Object.entries(s.maxBids)) {
    for (const m of Object.values(byBidder)) {
      if (m.status !== 'ACTIVE' || !pick(m)) continue;
      const seq = s.nextSeq;
      events.push({ type: 'MAX_BID_INVALIDATED', lotId, bidderId: m.bidderId, maxSeq: m.placedSeq, reason, reconfirmRequested, seq, channel: 'private', actorId, serverTs: now });
      s = { ...s, maxBids: setMax(s.maxBids, lotId, m.bidderId, { ...m, status: 'INVALIDATED', endedReason: reason }), ...bumpSeq(seq) };
    }
  }
  return { state: s, events };
}

// ---------------------------------------------------------------------------
// Selling groups - ADR-60 cut 5
// ---------------------------------------------------------------------------

/** Whether `lotId` is the lot in the ring or a member of its group. */
function lotInRing(state: RingState, lotId: string): boolean {
  return lotId === state.currentLotId || (state.sellingGroup !== null && state.sellingGroup.lotIds.includes(lotId));
}

/** ADR-70 cut 3 (Q4): the lot in the ring holds somebody's bid - open with a
 *  high bid, or hammered and not yet settled. The ring does not walk away
 *  from it (SET_CURRENT_LOT, and a reopen of an earlier lot). */
function lotHoldsBid(state: RingState): boolean {
  return (BIDDING_STATES.includes(state.lotState) && state.currentHighBid !== null) || state.lotState === 'HAMMERED_PENDING';
}

/**
 * ADR-70 cut 6c: the lot a REOPEN_LOT would bring back from a pass or a
 * no-sale right now, or null. The reducer's own rule, so a console offers
 * the button exactly when the ring would take it.
 */
export function reopenableDeparture(state: RingState): { readonly lotId: string; readonly outcome: DepartedLot['outcome'] } | null {
  const d = state.lastDeparted;
  if (d === null || state.finalizedSeq !== null || state.policy.mode !== 'LIVE') return null;
  if (state.currentLotId !== d.lotId && lotHoldsBid(state)) return null;
  return { lotId: d.lotId, outcome: d.outcome };
}

/**
 * ADR-70 cut 6i (hitlist 17.4): why REOPEN_TIMED_LOT would refuse this lot
 * right now, or null when it would take it. Only a lot that closed with no
 * sale reopens, and only from the close the request names: a sold lot is
 * VOID_SALE's, a lot still open or awaiting staff is not closed, and a lot
 * that has reopened (or closed again) since is a stale request. The
 * reducer's own rule, so the ring's door reports exactly what it would do.
 */
export function timedReopenRefusal(state: RingState, lotId: string, closeSeq: number): RejectionReason | null {
  if (state.finalizedSeq !== null) return 'SALE_FINALIZED';
  if (state.policy.mode !== 'TIMED') return 'ILLEGAL_TRANSITION';
  const lot = state.lots[lotId];
  if (!lot) return 'ILLEGAL_TRANSITION';
  if (lot.phase === 'SOLD' || standingSale(state, [lotId])) return 'LOT_ALREADY_SOLD';
  if (lot.phase !== 'NO_SALE') return 'ILLEGAL_TRANSITION';
  if (lot.closeSeq !== closeSeq) return 'STALE_AUCTION_STATE';
  return null;
}

/** The settlement facts of selling the ring's lot (or group) at `price`. */
function soldGroupOf(state: RingState, price: number, selectedLotIds: readonly string[] | undefined): { readonly group: SoldGroup | null; readonly reject: RejectionReason | null } {
  const g = state.sellingGroup;
  if (g === null) return { group: null, reject: null };
  let lotIds: readonly string[] = g.lotIds;
  let multiplier = 1;
  let remainder: readonly string[] = [];
  if (g.mode === 'CHOICE') {
    const picked = [...new Set(selectedLotIds ?? [])];
    // The lead is the lot the board named and LOT_SOLD carries; a pick that
    // leaves it out would sell "lot 42" without lot 42 in the group.
    if (picked.length === 0 || picked.some((id) => !g.lotIds.includes(id)) || !picked.includes(g.lotIds[0]!)) return { group: null, reject: 'SELECTION_REQUIRED' };
    lotIds = g.lotIds.filter((id) => picked.includes(id));
    multiplier = lotIds.length;
    remainder = g.lotIds.filter((id) => !picked.includes(id));
  } else if (g.mode === 'X_MONEY') {
    multiplier = g.multiplier;
  }
  return { group: { mode: g.mode, lotIds, unitPrice: price, multiplier, totalHammer: price * multiplier, remainderLotIds: remainder }, reject: null };
}

// ---------------------------------------------------------------------------
// Material update acknowledgement - ADR-60 cut 3
// ---------------------------------------------------------------------------

/**
 * The announcements on `lotId` (and sale-wide) that `bidderId` has not yet
 * acknowledged, oldest first. Pure and exported: the ring's eligibility answer,
 * the personal snapshot and the bid gate all read the same function.
 */
/**
 * The bound on `requiredAcks` (post-merge audit): at most this many required
 * announcements are kept per scope. Acknowledgement is a high-water mark
 * (ACKNOWLEDGE_ANNOUNCEMENTS through the latest seq), so the newest N are all
 * a bidder ever needs to read; older ones are superseded by "acknowledge
 * everything shown". With scopes bounded by the catalog plus the sale-wide
 * scope, the STATE blob's share is at most (lots + 1) * this.
 */
export const MAX_REQUIRED_ACKS_PER_SCOPE = 25;

/** A lot that is finished takes its required announcements with it. */
function withoutAckScope(acks: RingState['requiredAcks'], scope: string): RingState['requiredAcks'] {
  if (!(scope in acks)) return acks;
  const { [scope]: _gone, ...rest } = acks;
  void _gone;
  return rest;
}

/**
 * ADR-70 cut 6c: what REOPEN_LOT needs to bring `lotId` back exactly as it
 * leaves now - read from the state BEFORE the pass or no-sale changes it.
 */
function departureOf(state: RingState, lotId: string, outcome: DepartedLot['outcome'], seq: number): DepartedLot {
  return {
    lotId,
    outcome,
    seq,
    position: ringPosition(state),
    sellingGroup: state.sellingGroup,
    requiredAcks: state.requiredAcks[lotId] ?? null,
    previousLot: state.previousLot,
    incrementStep: chosenStepOf(state),
  };
}

export function unacknowledgedAnnouncements(state: RingState, bidderId: string, lotId: string): readonly RequiredAcknowledgement[] {
  const acks = state.acknowledgements[bidderId] ?? {};
  const out: RequiredAcknowledgement[] = [];
  for (const scope of [lotId, SALE_WIDE_SCOPE]) {
    const mark = acks[scope] ?? 0;
    for (const r of state.requiredAcks[scope] ?? []) if (r.seq > mark) out.push(r);
  }
  return out.sort((a, b) => a.seq - b.seq);
}

/** Accept `events` on `state`, then let proxies on `lotId` respond - all in ONE Decision. */
function acceptThenSettle(state: RingState, events: readonly DomainEvent[], lotId: string | null, now: number): Decision {
  if (lotId === null) return accept(state, events);
  const r = settleProxies(state, lotId, now);
  return accept(r.state, [...events, ...r.events]);
}

// ---------------------------------------------------------------------------
// Pending online bids - ADR-60 cut 2 (operator-accepted internet bids)
// ---------------------------------------------------------------------------

interface HoldInput {
  readonly bidderId: string;
  readonly sessionId: string;
  readonly lotId: string;
  readonly amount: number;
  readonly bidSource: BidSource;
  readonly proxyOfMaxSeq: number | null;
  readonly idempotencyKey: string | null;
}

/** Hold an internet bid as the ring's one pending bid. The board does not
 *  move; the crowd learns a bid is waiting (identity redacted like any bid). */
function holdPending(state: RingState, b: HoldInput, now: number, actorId: string): { readonly state: RingState; readonly events: DomainEvent[] } {
  const seq = state.nextSeq;
  const ttl = state.policy.confirm.pendingTtlMs;
  const pending: PendingBid = {
    id: `pend-${seq}`,
    bidderId: b.bidderId,
    sessionId: b.sessionId,
    lotId: b.lotId,
    amount: b.amount,
    bidSource: b.bidSource,
    proxyOfMaxSeq: b.proxyOfMaxSeq,
    idempotencyKey: b.idempotencyKey,
    createdSeq: seq,
    createdAt: now,
    expiresAt: ttl === null ? null : now + ttl,
  };
  const ev: DomainEvent = {
    type: 'ONLINE_BID_PENDING',
    pendingBidId: pending.id,
    lotId: b.lotId,
    amount: b.amount,
    bidSource: b.bidSource,
    bidderRef: bidderRef(b.bidderId, 'ONLINE'),
    bidderId: b.bidderId,
    proxyOfMaxSeq: b.proxyOfMaxSeq,
    expiresAt: pending.expiresAt,
    seq,
    channel: 'public',
    actorId,
    serverTs: now,
  };
  return { state: { ...state, pendingBid: pending, ...bumpSeq(seq) }, events: [ev] };
}

/** Resolve the pending bid with `result`. Never silent: the bidder, the floor
 *  and the crowd all learn how it ended from the log. No-op with none pending. */
function resolvePending(state: RingState, result: PendingResolution, now: number, actorId: string, reason: string | null = null): { readonly state: RingState; readonly events: DomainEvent[] } {
  const p = state.pendingBid;
  if (p === null) return { state, events: [] };
  const seq = state.nextSeq;
  const ev: DomainEvent = {
    type: 'PENDING_BID_RESOLVED',
    pendingBidId: p.id,
    lotId: p.lotId,
    amount: p.amount,
    bidderRef: bidderRef(p.bidderId, 'ONLINE'),
    bidderId: p.bidderId,
    result,
    reason,
    seq,
    channel: 'public',
    actorId,
    serverTs: now,
  };
  return { state: { ...state, pendingBid: null, ...bumpSeq(seq) }, events: [ev] };
}

// ---------------------------------------------------------------------------
// Equal bids - ADR-70 cut 2 (brief section 12)
// ---------------------------------------------------------------------------

interface EqualBidTake {
  readonly to: 'ONSITE' | 'ONLINE';
  readonly cause: 'OPERATOR' | 'POLICY';
  /** The winner's bidder id: the floor actor for ONSITE, the bidder for ONLINE. */
  readonly winnerId: string;
  readonly winnerPaddle: string | null;
  readonly reason: string;
  readonly actorId: string;
  readonly now: number;
}

/** A floor bid's paddle as typed, or null. The wire is untyped. */
function paddleOrNull(v: unknown): string | null {
  return typeof v === 'string' && v.trim().length > 0 ? v.trim() : null;
}

/**
 * The other side takes the standing bid at the SAME amount: the override is
 * recorded (EQUAL_BID_OVERRIDDEN), then the winner's bid is accepted through
 * the shared bid core. The ask does not move - the amount did not - so a
 * cut ask and a held internet bid at it both stay exactly as they were. The
 * displaced bid stays in the log; the new bid's undo record points back at
 * it. Callers have checked the lot is biddable and the standing bid is the
 * other side's.
 */
function takeEqualBid(state: RingState, t: EqualBidTake): { readonly state: RingState; readonly events: DomainEvent[] } {
  const lotId = state.currentLotId!;
  const amount = state.currentHighBid!;
  const displacedSource = state.currentHighBidSource!;
  const displacedId = state.currentHighBidder!;
  const displacedSeq = state.currentHighBidSeq!;
  const winnerSource: BidSource = t.to === 'ONSITE' ? 'FLOOR' : 'ONLINE';
  const oseq = state.nextSeq;
  const bseq = oseq + 1;
  const override: DomainEvent = {
    type: 'EQUAL_BID_OVERRIDDEN',
    lotId,
    amount,
    awardedTo: t.to,
    cause: t.cause,
    policy: state.policy.equalBid.policy,
    displacedBidSeq: displacedSeq,
    displacedBidSource: displacedSource,
    displacedBidderRef: bidderRef(displacedId, displacedSource),
    displacedBidderId: displacedId,
    displacedPaddle: state.currentHighPaddle,
    winnerBidSeq: bseq,
    winnerBidSource: winnerSource,
    winnerBidderRef: bidderRef(t.winnerId, winnerSource),
    winnerBidderId: t.winnerId,
    winnerPaddle: t.winnerPaddle,
    restoresSeq: null,
    reason: t.reason,
    seq: oseq,
    channel: 'public',
    actorId: t.actorId,
    serverTs: t.now,
  };
  const r = acceptBidIntoPosition(ringPosition(state), state.incrementTableId, {
    lotId, bidderId: t.winnerId, amount, source: winnerSource, paddleNumber: t.winnerPaddle, seq: bseq, now: t.now, lotState: state.lotState,
  }, chosenStepOf(state));
  const accepted = r.event as Extract<DomainEvent, { readonly type: 'BID_ACCEPTED' }>;
  const keepAsk = state.currentAsk ?? accepted.nextAsk;
  const award: EqualBidAward | null = t.to === 'ONSITE'
    ? { overrideSeq: oseq, lotId, floorBidSeq: bseq, displacedBidSeq: displacedSeq, displacedBidderId: displacedId, displacedBidSource: displacedSource }
    : null;
  return {
    state: { ...withRingPosition({ ...state, ...bumpSeq(bseq) }, { ...r.pos, currentAsk: keepAsk }), equalBidAward: award },
    events: [override, { ...accepted, nextAsk: keepAsk }],
  };
}

// ---------------------------------------------------------------------------

export function reduce(state: RingState, command: Command): Decision {
  const { actor, now } = command;
  const seq = state.nextSeq;
  const bump = bumpSeq;
  const timed = state.policy.mode === 'TIMED';

  // Settlement fence. Once the sale is finalized the ring is a sealed record: it
  // accepts NO further commands - money-changing or otherwise - so SALE_FINALIZED
  // is provably the last event and its seq is the authoritative final sequence.
  // This is exactly what lets close treat the log as complete; without the
  // fence, an empty read from a still-running ring is not proof of finality and
  // a late SOLD could be dropped from a "final" settlement.
  if (state.finalizedSeq !== null) return reject(state, 'SALE_FINALIZED');

  // Mode fences: the live clerk lifecycle on a TIMED ring, or a timed open on a
  // LIVE ring, is refused - one lifecycle per ring.
  if (timed && LIVE_ONLY_COMMANDS.has(command.type)) return reject(state, 'ILLEGAL_TRANSITION');
  if (!timed && (command.type === 'OPEN_TIMED_LOT' || command.type === 'REOPEN_TIMED_LOT')) return reject(state, 'ILLEGAL_TRANSITION');

  // ADR-60 cut 5: a bid, a max or a cancel naming any lot of the group in the
  // ring is about the group, and the group is one board led by the current
  // lot. Re-point it at the lead once, then judge it exactly like any other.
  if (
    !timed &&
    state.sellingGroup !== null &&
    state.currentLotId !== null &&
    (command.type === 'PLACE_BID' || command.type === 'PLACE_FLOOR_BID' || command.type === 'PLACE_MAX_BID' || command.type === 'CANCEL_MAX_BID') &&
    command.lotId !== state.currentLotId &&
    state.sellingGroup.lotIds.includes(command.lotId)
  ) {
    return reduce(state, { ...command, lotId: state.currentLotId });
  }

  // ADR-70: a clerk command that settles or moves the lot in the ring is
  // judged against the ring. Clerks only - anyone else is refused by the
  // command's own NOT_AUTHORIZED below, which must keep winning. A command
  // naming a member of the group in the ring is about the group: re-pointed
  // at the lead exactly as bids are, then judged.
  if (!timed && actor.role === 'clerk' && isStaleGuarded(command)) {
    if (
      namesLotInRing(command) &&
      state.sellingGroup !== null &&
      state.currentLotId !== null &&
      command.lotId !== state.currentLotId &&
      state.sellingGroup.lotIds.includes(command.lotId)
    ) {
      return reduce(state, { ...command, lotId: state.currentLotId });
    }
    const stale = staleView(state, command);
    if (stale) return stale;
  }

  switch (command.type) {
    // -----------------------------------------------------------------------
    // Floor-input liveness + time
    // -----------------------------------------------------------------------
    case 'HEARTBEAT': {
      if (!isFloorInput(actor.role)) return reject(state, 'NOT_AUTHORIZED');
      // Liveness is state, not an event: heartbeats must not consume sequence
      // numbers or they would flood the log at several per second.
      return accept({ ...state, floorInputHealthyAt: now }, []);
    }

    case 'TICK': {
      // Engine-issued (clerk posture). A spotter's socket may not drive time.
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      // TIMED: the authoritative close sweep - the ONLY thing that closes a
      // timed lot. LIVE: the floor-input auto-pause rule, and (ADR-60) the
      // pending online bid's expiry - decided by engine time on commit, so
      // `step()` resolves an expired pending bid before any command that
      // would act on it (an ACCEPT after expiry is NO_PENDING_BID, always).
      const swept = sweepTimedCloses(state, now);
      let s = swept.state;
      const events: DomainEvent[] = [...swept.events];
      if (s.pendingBid !== null && s.pendingBid.expiresAt !== null && now >= s.pendingBid.expiresAt) {
        const lotId = s.pendingBid.lotId;
        const r = resolvePending(s, 'EXPIRED', now, ENGINE_ACTOR);
        s = r.state;
        events.push(...r.events);
        // Proxies were waiting behind it.
        const p = settleProxies(s, lotId, now);
        s = p.state;
        events.push(...p.events);
      }
      const paused = autoPause(s, now, s.nextSeq, actor.id);
      if (!paused) return accept(s, events);
      return accept(
        { ...s, onlineStatus: 'PAUSED', pauseReason: 'FLOOR_INPUT_LOST', ...bump(s.nextSeq) },
        [...events, paused],
      );
    }

    // -----------------------------------------------------------------------
    // Settlement fence
    // -----------------------------------------------------------------------
    case 'FINALIZE_SALE': {
      // Clerk-only, same posture as HAMMER / CONFIRM_SOLD. Finalization is
      // irreversible and bricks the ring, so a spotter must never be able to
      // end the sale. (Reached only when NOT already finalized - the fence above
      // short-circuits a repeat.)
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      // Never seal mid-lot: an active/provisional lot must be resolved
      // (SOLD / NO_SALE / PASSED) first, so an accidental End Sale while a horse
      // is still live cannot permanently brick the ring.
      if (state.lotState === 'OPEN' || state.lotState === 'FAIR_WARNING' || state.lotState === 'HAMMERED_PENDING') {
        return reject(state, 'ILLEGAL_TRANSITION');
      }
      // TIMED: every lot must have reached a terminal disposition.
      if (Object.values(state.lots).some((l) => l.phase === 'OPEN' || l.phase === 'EXTENDED' || l.phase === 'CLOSED_PENDING')) {
        return reject(state, 'ILLEGAL_TRANSITION');
      }
      return accept(
        { ...state, finalizedSeq: seq, ...bump(seq) },
        [{ type: 'SALE_FINALIZED', finalSeq: seq, seq, channel: 'public', actorId: actor.id, serverTs: now }],
      );
    }

    // -----------------------------------------------------------------------
    // Lot lifecycle - clerk only (LIVE)
    // -----------------------------------------------------------------------
    case 'SET_CURRENT_LOT': {
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      // ADR-70 cut 3 (Q4): a lot holding a bid is not left behind. Open with
      // a high bid, or hammered and not yet settled: somebody's bid stands,
      // and walking away from it strands them. Sell, pass or no-sale first.
      // An open lot with NO bid may still be left (nothing is stranded).
      if (state.currentLotId !== null && lotHoldsBid(state)) return reject(state, 'LOT_IN_PROGRESS');
      // A held internet bid on the lot being left is closed out, visibly,
      // before the ring moves on (it would otherwise wait on a lot that is
      // gone until the next open dropped it without a word).
      const pr = resolvePending(state, 'LOT_CLOSED', now, actor.id);
      const lseq = pr.state.nextSeq;
      return accept(
        // ADR-70 cut 6d: a chosen increment was for the lot that leaves.
        { ...pr.state, currentLotId: command.lotId, lotState: 'UPCOMING', closeStep: null, sellingGroup: null, equalBidAward: null, chosenIncrement: null, ...bump(lseq) },
        [...pr.events, { type: 'CURRENT_LOT_SET', lotId: command.lotId, seq: lseq, channel: 'public', actorId: actor.id, serverTs: now }],
      );
    }

    case 'OPEN_LOT': {
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      if (command.lotId !== state.currentLotId) return reject(state, 'LOT_NOT_OPEN');
      if (state.lotState !== 'UPCOMING') return reject(state, 'ILLEGAL_TRANSITION');
      // ADR-65: the guard that ends the silent re-run. Going BACK to a sold
      // lot stays legal (that is how the clerk voids it from the board), but
      // nothing sells it, alone or inside a group, while its sale stands.
      if (standingSale(state, command.group ? command.group.lotIds : [command.lotId])) return reject(state, 'LOT_ALREADY_SOLD');
      // ADR-60 cut 5: a group sells on the ONE bid; the lead is the lot the
      // board names. Distinct ids, the lead first, a positive multiplier.
      let group: SellingGroup | null = null;
      if (command.group) {
        const ids = command.group.lotIds;
        if (ids.length < 2 || ids[0] !== command.lotId || new Set(ids).size !== ids.length) return reject(state, 'ILLEGAL_TRANSITION');
        // X_MONEY: "so much money times the head". The multiplier IS the lot
        // count; an explicit one must agree, or the settlement rows (one per
        // lot at the unit price) would not sum to the hammer.
        const multiplier = command.group.mode === 'X_MONEY' ? (command.group.multiplier ?? ids.length) : 1;
        if (!Number.isFinite(multiplier) || multiplier < 1 || (command.group.mode === 'X_MONEY' && multiplier !== ids.length)) return reject(state, 'INVALID_INCREMENT');
        group = { lotIds: [...ids], mode: command.group.mode, multiplier };
      }
      const opened: RingState = {
        ...state,
        lotState: 'OPEN',
        closeStep: null,
        sellingGroup: group,
        currentAsk: command.openingAsk,
        currentHighBid: null,
        currentHighBidder: null,
        currentHighBidSource: null,
        currentHighPaddle: null,
        currentHighBidSeq: null,
        // A new lot has no reversible bid; the previous lot's undo record
        // must not leak across the boundary.
        lastBid: null,
        pendingBid: null,
        equalBidAward: null,
        // ADR-70 cut 6c: a re-offer is a fresh start for the lot, so the board
        // it left with is no longer one a reopen may bring back.
        lastDeparted: state.lastDeparted !== null && (group ? group.lotIds : [command.lotId]).includes(state.lastDeparted.lotId) ? null : state.lastDeparted,
        ...bump(seq),
      };
      const ev: DomainEvent = {
        type: 'LOT_OPENED',
        lotId: command.lotId,
        openingAsk: command.openingAsk,
        incrementTableId: state.incrementTableId,
        ...(group ? { group } : {}),
        seq,
        channel: 'public',
        actorId: actor.id,
        serverTs: now,
      };
      // Maxes placed on this lot before it opened (a live "I'll go to twenty"
      // while it was up, or a pre-bid) run now from the opening ask - unless
      // policy keeps them dormant until the first live bid. The handoff is
      // marked in the log BEFORE the proxies run, so the sequence reads
      // LOT_OPENED → PREBID_HANDOFF → BID_ACCEPTED(PROXY)… (ADR-42 §3).
      if (state.policy.prebid.carryIn === 'NONE') return accept(opened, [ev]);
      const activeMaxCount = Object.values(opened.maxBids[command.lotId] ?? {}).filter((m) => m.status === 'ACTIVE').length;
      if (activeMaxCount === 0) return acceptThenSettle(opened, [ev], command.lotId, now);
      const hseq = opened.nextSeq;
      const handoff: DomainEvent = { type: 'PREBID_HANDOFF', lotId: command.lotId, activeMaxCount, openingAsk: command.openingAsk, seq: hseq, channel: 'private', actorId: actor.id, serverTs: now };
      return acceptThenSettle({ ...opened, ...bump(hseq) }, [ev, handoff], command.lotId, now);
    }

    case 'SET_ASK': {
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      if (!BIDDING_STATES.includes(state.lotState)) return reject(state, 'LOT_NOT_OPEN');
      // The ask may be cut below the increment ladder (a clerk takes "five
      // seventy-five"), but never to or below the standing high bid: the next
      // bid must BEAT the board. Found by the M5 console: a cut under a
      // standing high bid would otherwise become the new high.
      if (!Number.isFinite(command.ask) || command.ask <= 0) return reject(state, 'INVALID_INCREMENT');
      if (state.currentHighBid !== null && command.ask <= state.currentHighBid) return reject(state, 'BELOW_HIGH_BID');
      // ADR-60: a pending online bid was at the OLD ask; it is resolved
      // ASK_CHANGED before the ask moves (the bidder is told, and may re-bid).
      const pr = resolvePending(state, 'ASK_CHANGED', now, actor.id);
      const askSeq = pr.state.nextSeq;
      return acceptThenSettle(
        { ...pr.state, currentAsk: command.ask, ...bump(askSeq) },
        [...pr.events, { type: 'ASK_SET', lotId: command.lotId, ask: command.ask, reason: command.reason ?? null, seq: askSeq, channel: 'public', actorId: actor.id, serverTs: now }],
        state.currentLotId,
        now,
      );
    }

    case 'DECLARE_FAIR_WARNING': {
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      if (state.lotState !== 'OPEN') return reject(state, 'ILLEGAL_TRANSITION');
      return accept({ ...state, lotState: 'FAIR_WARNING', closeStep: null, ...bump(seq) }, [
        { type: 'FAIR_WARNING', lotId: command.lotId, seq, channel: 'public', actorId: actor.id, serverTs: now },
      ]);
    }

    case 'SET_CLOSE_STEP': {
      // ADR-70 cut 3 (hitlist section 6): the close, step by step, validated
      // here and recorded as a public fact so bidders see it as it is said.
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      if (!BIDDING_STATES.includes(state.lotState)) return reject(state, 'ILLEGAL_TRANSITION');
      const step: unknown = command.step;
      if (step !== null && !isCloseStep(step)) return reject(state, 'ILLEGAL_TRANSITION');
      // The step already standing is not a new fact (a double tap), and
      // "back to open" on a lot that is open is nothing at all.
      const standing = state.lotState === 'FAIR_WARNING' ? state.closeStep : null;
      if (step === standing && (step !== null || state.lotState === 'OPEN')) return reject(state, 'ILLEGAL_TRANSITION');
      return accept({ ...state, lotState: step === null ? 'OPEN' : 'FAIR_WARNING', closeStep: step, ...bump(seq) }, [
        { type: 'CLOSE_STEP_SET', lotId: command.lotId, step, previousStep: standing, seq, channel: 'public', actorId: actor.id, serverTs: now },
      ]);
    }

    case 'HAMMER': {
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      if (!BIDDING_STATES.includes(state.lotState)) return reject(state, 'ILLEGAL_TRANSITION');
      if (state.currentHighBid === null || state.currentHighBidder === null) {
        // Nothing bid: this is a pass, not a hammer. (A PENDING internet bid is
        // not a bid until the operator accepts it - ADR-60.)
        return reject(state, 'ILLEGAL_TRANSITION');
      }
      // ADR-60: a pending internet bid the operator never took is closed out
      // by the hammer, visibly, before the hammer commits.
      const pr = resolvePending(state, 'LOT_CLOSED', now, actor.id);
      const hseq = pr.state.nextSeq;
      return accept({ ...pr.state, lotState: 'HAMMERED_PENDING', closeStep: null, ...bump(hseq) }, [
        ...pr.events,
        {
          type: 'HAMMER',
          lotId: command.lotId,
          finalBid: state.currentHighBid,
          winningBidderRef: bidderRef(state.currentHighBidder, state.currentHighBidSource ?? 'ONLINE'),
          bidSource: state.currentHighBidSource ?? 'ONLINE',
          seq: hseq,
          channel: 'public',
          actorId: actor.id,
          serverTs: now,
        },
      ]);
    }

    case 'CONFIRM_SOLD': {
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      if (timed) {
        // TIMED with autoDisposition off: staff confirm a CLOSED_PENDING lot.
        // (Staff may sell below a reserve - that is exactly the human decision
        // manual disposition exists for.)
        const lot = state.lots[command.lotId];
        if (!lot || lot.phase !== 'CLOSED_PENDING') return reject(state, 'ILLEGAL_TRANSITION');
        if (lot.currentHighBid === null || lot.currentHighBidder === null) return reject(state, 'ILLEGAL_TRANSITION');
        const d = disposeTimedLot(state, lot, now, actor.id, null);
        return accept(d.state, d.events);
      }
      if (state.lotState !== 'HAMMERED_PENDING') return reject(state, 'ILLEGAL_TRANSITION');
      if (state.currentHighBid === null || state.currentHighBidder === null) {
        return reject(state, 'ILLEGAL_TRANSITION');
      }
      // "Number, please?" A floor bid is taken from an anonymous raised hand,
      // so the winner is identified here rather than on every bid during the
      // chant. The paddle a bid was ALREADY placed with always wins: this
      // fills a blank, it never overwrites an attribution made at the time.
      // Ignored outright for an online winner, who is known by their account.
      const late =
        state.currentHighBidSource === 'FLOOR' && state.currentHighPaddle === null
          ? (command.winningPaddleNumber ?? null)
          : null;
      const winningPaddle = state.currentHighPaddle ?? late;
      // ADR-60 cut 5: what the bid MEANT for a group - the lots that sold, the
      // multiplier and the total, computed here and never by a client.
      const sg = soldGroupOf(state, state.currentHighBid, command.selectedLotIds);
      if (sg.reject !== null) return reject(state, sg.reject);
      const soldEvents: DomainEvent[] = [
        {
          type: 'LOT_SOLD',
          lotId: state.currentLotId ?? command.lotId,
          price: state.currentHighBid,
          buyerId: state.currentHighBidder,
          bidSource: state.currentHighBidSource ?? 'ONLINE',
          pricingMode: command.pricingMode,
          // Self-contained settlement fact (M6): the buyer's paddle (floor) and
          // the winning bid seq travel ON the event, so the projector never
          // correlates back through history. Redacted for the public channel.
          winningPaddleNumber: winningPaddle,
          winningBidSeq: state.currentHighBidSeq,
          dispositionCause: 'HAMMER',
          ...(sg.group ? { group: sg.group } : {}),
          seq,
          channel: 'public',
          actorId: actor.id,
          serverTs: now,
        },
      ];
      if (sg.group && sg.group.remainderLotIds.length > 0) {
        soldEvents.push({ type: 'LOT_GROUP_REMAINDER', lotIds: sg.group.remainderLotIds, soldLotIds: sg.group.lotIds, seq: seq + 1, channel: 'public', actorId: actor.id, serverTs: now });
      }
      return accept(
        {
          ...state,
          lotState: 'SOLD',
          sellingGroup: null,
          // ADR-64: the last disposition, for a client joining between lots.
          previousLot: { lotId: state.currentLotId ?? command.lotId, outcome: 'SOLD', price: state.currentHighBid, seq, at: now },
          // ADR-65: the ring remembers what it sold - every lot of a group.
          sales: withSales(state.sales, sg.group ? sg.group.lotIds : [state.currentLotId ?? command.lotId], {
            soldSeq: seq,
            price: state.currentHighBid,
            bidSource: state.currentHighBidSource ?? 'ONLINE',
            buyerId: state.currentHighBidder,
            paddle: winningPaddle,
          }),
          requiredAcks: withoutAckScope(state.requiredAcks, command.lotId),
          // ADR-70 cut 6c: once a lot sells, an earlier pass is history; it
          // comes back by a re-offer, never by a reopen.
          lastDeparted: null,
          ...bump(seq + soldEvents.length - 1),
        },
        soldEvents,
      );
    }

    case 'DECLARE_NO_SALE': {
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      if (timed) {
        const lot = state.lots[command.lotId];
        if (!lot || lot.phase !== 'CLOSED_PENDING') return reject(state, 'ILLEGAL_TRANSITION');
        const d = disposeTimedLot(state, lot, now, actor.id, 'CLERK');
        return accept(d.state, d.events);
      }
      if (state.lotState !== 'HAMMERED_PENDING') return reject(state, 'ILLEGAL_TRANSITION');
      return accept(
        {
          ...state,
          lotState: 'NO_SALE',
          sellingGroup: null,
          previousLot: { lotId: command.lotId, outcome: 'NO_SALE', price: state.currentHighBid, seq, at: now },
          requiredAcks: withoutAckScope(state.requiredAcks, command.lotId),
          lastDeparted: departureOf(state, command.lotId, 'NO_SALE', seq),
          ...bump(seq),
        },
        [{ type: 'LOT_NO_SALE', lotId: command.lotId, cause: 'CLERK', seq, channel: 'public', actorId: actor.id, serverTs: now }],
      );
    }

    // -----------------------------------------------------------------------
    // ADR-65: a reversed sale is a new fact, never an erased one
    // -----------------------------------------------------------------------
    case 'VOID_SALE': {
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      // A recorded operational decision needs a reason (the ADR-60 precedent).
      if (typeof command.reason !== 'string' || command.reason.trim() === '') return reject(state, 'REASON_REQUIRED');
      const sale = state.sales[command.lotId];
      // Named by lot AND by the LOT_SOLD seq: a stale console can never void a
      // resale it has not seen.
      if (!sale || sale.soldSeq !== command.soldSeq) return reject(state, 'NO_SUCH_SALE');
      if (sale.voidedSeq !== null) return reject(state, 'SALE_ALREADY_VOIDED');
      // A group sale is reversed as a whole (section 6.6): every member entry
      // that shares the soldSeq is marked in the one commit.
      const lotIds = sale.groupLotIds ?? [command.lotId];
      const sales: Record<string, RingSale> = { ...state.sales };
      for (const id of lotIds) {
        const member = sales[id];
        if (member && member.soldSeq === sale.soldSeq) sales[id] = { ...member, voidedSeq: seq };
      }
      const ev: DomainEvent = {
        type: 'SALE_VOIDED',
        lotId: command.lotId,
        soldSeq: sale.soldSeq,
        kind: command.kind,
        reason: command.reason,
        price: sale.price,
        bidSource: sale.bidSource,
        buyerRef: bidderRef(sale.buyerId, sale.bidSource),
        buyerId: sale.buyerId,
        winningPaddleNumber: sale.paddle,
        ...(sale.groupLotIds ? { group: { lotIds: [...sale.groupLotIds] } } : {}),
        seq,
        channel: 'public',
        actorId: actor.id,
        serverTs: now,
      };
      // previousLot said "sold" about this very sale: it now says "voided", so
      // a client joining between lots is not shown a sale that does not stand.
      const voided: PreviousLot = { lotId: command.lotId, outcome: 'VOIDED', price: sale.price, seq, at: now };
      const previousLot: PreviousLot | null = state.previousLot !== null && state.previousLot.seq === sale.soldSeq ? voided : state.previousLot;
      // ADR-70 cut 6c: the same for the previous lot a reopen would restore.
      const d = state.lastDeparted;
      const lastDeparted = d !== null && d.previousLot !== null && d.previousLot.seq === sale.soldSeq ? { ...d, previousLot: voided } : d;
      let next: RingState = { ...state, sales, previousLot, lastDeparted, ...bump(seq) };
      if (timed) {
        // The lot gets a second life: OPEN_TIMED_LOT accepts a VOIDED lot.
        for (const id of lotIds) {
          const lot = next.lots[id];
          if (lot) next = withLot(next, { ...lot, phase: 'VOIDED' });
        }
        return accept(next, [ev]);
      }
      // The board: if the voided lot is up and reads SOLD, it becomes exactly
      // what SET_CURRENT_LOT leaves (staged, nothing on the board) so the clerk
      // can re-offer at once or move on. A void discovered three hips later
      // does not disturb the horse in the ring.
      if (state.currentLotId !== null && lotIds.includes(state.currentLotId) && state.lotState === 'SOLD') {
        next = {
          ...next,
          lotState: 'UPCOMING',
          sellingGroup: null,
          currentAsk: null,
          currentHighBid: null,
          currentHighBidder: null,
          currentHighBidSource: null,
          currentHighPaddle: null,
          currentHighBidSeq: null,
          lastBid: null,
          pendingBid: null,
        };
      }
      return accept(next, [ev]);
    }

    case 'DECLARE_PASSED': {
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      if (!BIDDING_STATES.includes(state.lotState)) return reject(state, 'ILLEGAL_TRANSITION');
      const pr = resolvePending(state, 'LOT_CLOSED', now, actor.id);
      const pseq = pr.state.nextSeq;
      return accept(
        {
          ...pr.state,
          lotState: 'PASSED',
          closeStep: null,
          sellingGroup: null,
          previousLot: { lotId: command.lotId, outcome: 'PASSED', price: pr.state.currentHighBid, seq: pseq, at: now },
          requiredAcks: withoutAckScope(pr.state.requiredAcks, command.lotId),
          lastDeparted: departureOf(pr.state, command.lotId, 'PASSED', pseq),
          ...bump(pseq),
        },
        [...pr.events, { type: 'LOT_PASSED', lotId: command.lotId, seq: pseq, channel: 'public', actorId: actor.id, serverTs: now }],
      );
    }

    case 'SELL_AND_ADVANCE':
    case 'PASS_AND_ADVANCE': {
      // ADR-70 cut 3 (hitlist section 13): the disposition and the move to
      // the next lot as ONE decision. Each half is the ordinary command,
      // judged by the ordinary rules (the sale by CONFIRM_SOLD, the pass by
      // DECLARE_PASSED, the move by SET_CURRENT_LOT); both commit or neither
      // does. The next lot is staged, never opened.
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      if (typeof command.nextLotId !== 'string' || command.nextLotId === '' || command.nextLotId === command.lotId) {
        return reject(state, 'ILLEGAL_TRANSITION');
      }
      const disposition: Command = command.type === 'SELL_AND_ADVANCE'
        ? {
            type: 'CONFIRM_SOLD', actor, now, lotId: command.lotId, pricingMode: command.pricingMode,
            ...(command.winningPaddleNumber !== undefined ? { winningPaddleNumber: command.winningPaddleNumber } : {}),
            ...(command.selectedLotIds !== undefined ? { selectedLotIds: command.selectedLotIds } : {}),
          }
        : { type: 'DECLARE_PASSED', actor, now, lotId: command.lotId };
      const settled = reduce(state, disposition);
      if (settled.status === 'rejected') return reject(state, settled.reason);
      const moved = reduce(settled.state, { type: 'SET_CURRENT_LOT', actor, now, lotId: command.nextLotId });
      if (moved.status === 'rejected') return reject(state, moved.reason);
      return accept(moved.state, [...settled.events, ...moved.events]);
    }

    case 'REOPEN_LOT': {
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      // From the hammer: the lot in the ring (or a member of its group) goes
      // back to open bidding with its board as it stands. Once SOLD has
      // committed, the sale is a fact and reversing it is VOID_SALE, never a
      // reopen (spec 8.9).
      if (state.lotState === 'HAMMERED_PENDING' && lotInRing(state, command.lotId)) {
        const lotId = state.currentLotId ?? command.lotId;
        return acceptThenSettle(
          { ...state, lotState: 'OPEN', ...bump(seq) },
          [{ type: 'LOT_REOPENED', lotId, reason: command.reason, from: 'HAMMERED', seq, channel: 'public', actorId: actor.id, serverTs: now }],
          lotId,
          now,
        );
      }
      // ADR-70 cut 6c (hitlist 17.3): a lot that passed or no-saled comes
      // back exactly as it left - the lot in the ring, or the last one that
      // left while the lot now in the ring holds no bid (the SET_CURRENT_LOT
      // rule: nobody's bid is stranded). Anything older is re-offered fresh.
      const d = state.lastDeparted;
      if (d === null || d.lotId !== command.lotId) return reject(state, command.lotId === state.currentLotId ? 'ILLEGAL_TRANSITION' : 'STALE_AUCTION_STATE');
      const reason = typeof command.reason === 'string' ? command.reason.trim() : '';
      if (reason.length === 0) return reject(state, 'REASON_REQUIRED');
      const leaving = state.currentLotId !== d.lotId;
      if (leaving && lotHoldsBid(state)) return reject(state, 'LOT_IN_PROGRESS');
      // A held internet bid on the lot being left is closed out first, as
      // SET_CURRENT_LOT does (none can stand on a lot that passed).
      const pr = resolvePending(state, 'LOT_CLOSED', now, actor.id);
      const events: DomainEvent[] = [...pr.events];
      let s = pr.state;
      if (leaving) {
        const cseq = s.nextSeq;
        events.push({ type: 'CURRENT_LOT_SET', lotId: d.lotId, seq: cseq, channel: 'public', actorId: actor.id, serverTs: now });
        s = { ...s, ...bump(cseq) };
      }
      const rseq = s.nextSeq;
      const p = d.position;
      events.push({
        type: 'LOT_REOPENED',
        lotId: d.lotId,
        reason,
        from: d.outcome,
        restoredHighBid: p.currentHighBid,
        restoredAsk: p.currentAsk,
        restoredBidSource: p.currentHighBidSource,
        restoredBidderRef: p.currentHighBidder !== null ? bidderRef(p.currentHighBidder, p.currentHighBidSource ?? 'ONLINE') : null,
        seq: rseq,
        channel: 'public',
        actorId: actor.id,
        serverTs: now,
      });
      // The departure's own previousLot gives way to the one before it: the
      // lot is in the ring again, so "last result" is not about it any more.
      const previousLot = s.previousLot !== null && s.previousLot.seq === d.seq ? d.previousLot : s.previousLot;
      return acceptThenSettle(
        {
          ...s,
          ...p,
          currentLotId: d.lotId,
          lotState: 'OPEN',
          closeStep: null,
          sellingGroup: d.sellingGroup,
          requiredAcks: d.requiredAcks !== null ? { ...s.requiredAcks, [d.lotId]: d.requiredAcks } : s.requiredAcks,
          previousLot,
          lastDeparted: null,
          // ADR-70 cut 6d: the increment the clerk chose comes back with it.
          chosenIncrement: typeof d.incrementStep === 'number' ? { lotId: d.lotId, step: d.incrementStep } : null,
          ...bump(rseq),
        },
        events,
        d.lotId,
        now,
      );
    }

    case 'RETRACT_LAST_BID': {
      // ADR-70 cut 6c (hitlist 17.1): CORRECT_FLOOR_BID for every source. The
      // same tip-only rule: a bid others have bid on top of is reconciliation.
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      const reason = typeof command.reason === 'string' ? command.reason.trim() : '';
      if (reason.length === 0) return reject(state, 'REASON_REQUIRED');
      const last = state.lastBid;
      if (last === null || last.seq !== command.reversesSeq) return reject(state, 'CORRECTION_REQUIRES_RECONCILIATION');
      // After the hammer the provisional winner is public: reopen first.
      if (!BIDDING_STATES.includes(state.lotState)) return reject(state, 'CORRECTION_REQUIRES_RECONCILIATION');
      const lotId = state.currentLotId ?? command.lotId;
      // The board goes back to the pre-bid ask: a held bid at this ask is
      // resolved ASK_CHANGED first, as for a correction.
      const pr = resolvePending(state, 'ASK_CHANGED', now, actor.id);
      const rseq = pr.state.nextSeq;
      let s: RingState = {
        ...pr.state,
        currentHighBid: last.previousHighBid,
        currentHighBidder: last.previousHighBidder,
        currentHighBidSource: last.previousHighBidSource,
        currentHighPaddle: last.previousHighPaddle,
        currentHighBidSeq: last.previousHighBidSeq,
        currentAsk: last.previousAsk,
        // A close called on the bid taken back no longer stands: the lot is
        // open again and the auctioneer calls the close afresh (as after a
        // bid during the close, ADR-70 cut 3).
        lotState: 'OPEN',
        closeStep: null,
        // One level of undo, as for a correction: the bid under this one has
        // bids of its own history and is reconciliation.
        lastBid: null,
        equalBidAward: null,
        ...bump(rseq),
      };
      const events: DomainEvent[] = [
        ...pr.events,
        {
          type: 'BID_RETRACTED',
          lotId,
          reversesSeq: last.seq,
          reason,
          amount: last.amount,
          bidSource: last.source,
          bidderRef: bidderRef(last.bidderId, last.source),
          bidderId: last.bidderId,
          restoredHighBid: last.previousHighBid,
          restoredAsk: last.previousAsk,
          restoredBidSource: last.previousHighBidSource,
          seq: rseq,
          channel: 'public',
          actorId: actor.id,
          serverTs: now,
        },
      ];
      // An online bidder's standing max would place the same bid again at
      // once: it ends, visibly, with the retraction named as the reason.
      if (last.source !== 'FLOOR') {
        const inv = invalidateMaxes(s, (m) => m.lotId === lotId && m.bidderId === last.bidderId, `BID_RETRACTED:${rseq}`, false, now, actor.id);
        s = inv.state;
        events.push(...inv.events);
      }
      return acceptThenSettle(s, events, lotId, now);
    }

    case 'RESET_BIDDING': {
      // ADR-70 cut 6c (hitlist 17.2): the board goes back to nothing and the
      // lot restarts at the operator's ask. Nothing is erased: every bid
      // stays in the log, and the history marks them reset.
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      if (!BIDDING_STATES.includes(state.lotState)) return reject(state, 'LOT_NOT_OPEN');
      const reason = typeof command.reason === 'string' ? command.reason.trim() : '';
      if (reason.length === 0) return reject(state, 'REASON_REQUIRED');
      if (typeof command.ask !== 'number' || !Number.isFinite(command.ask) || command.ask <= 0) return reject(state, 'INVALID_INCREMENT');
      const lotId = state.currentLotId ?? command.lotId;
      const clear = command.clearMaxBids === true;
      const activeMaxes = Object.values(state.maxBids[lotId] ?? {}).filter((m) => m.status === 'ACTIVE').length;
      // A reset with nothing to reset is not a fact (a second tap, or SET_ASK
      // by another name).
      if (state.currentHighBid === null && state.pendingBid === null && !(clear && activeMaxes > 0)) return reject(state, 'ILLEGAL_TRANSITION');
      const pr = resolvePending(state, 'ASK_CHANGED', now, actor.id);
      const rseq = pr.state.nextSeq;
      let s: RingState = {
        ...pr.state,
        lotState: 'OPEN',
        closeStep: null,
        currentHighBid: null,
        currentHighBidder: null,
        currentHighBidSource: null,
        currentHighPaddle: null,
        currentHighBidSeq: null,
        currentAsk: command.ask,
        lastBid: null,
        equalBidAward: null,
        ...bump(rseq),
      };
      const events: DomainEvent[] = [
        ...pr.events,
        {
          type: 'BIDDING_RESET',
          lotId,
          reason,
          ask: command.ask,
          resetHighBid: state.currentHighBid,
          resetBidSeq: state.currentHighBidSeq,
          clearMaxBids: clear,
          clearedMaxBids: clear ? activeMaxes : 0,
          seq: rseq,
          channel: 'public',
          actorId: actor.id,
          serverTs: now,
        },
      ];
      if (clear) {
        const inv = invalidateMaxes(s, (m) => m.lotId === lotId, `BIDDING_RESET:${rseq}`, false, now, actor.id);
        s = inv.state;
        events.push(...inv.events);
      }
      // Maxes kept on purpose bid again from the restart ask.
      return acceptThenSettle(s, events, lotId, now);
    }

    case 'SET_INCREMENT': {
      // ADR-70 cut 6d (hitlist 14): the clerk changes how the lot in the ring
      // bids up ("take fifties"), or goes back to the sale's table (null). The
      // choice is the lot's: SET_CURRENT_LOT clears it, a reopen brings it back.
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      if (state.currentLotId === null || command.lotId !== state.currentLotId) return reject(state, 'LOT_NOT_OPEN');
      const bidding = BIDDING_STATES.includes(state.lotState);
      if (!bidding && state.lotState !== 'UPCOMING') return reject(state, 'LOT_NOT_OPEN');
      const step: unknown = command.step;
      if (step !== null && !(typeof step === 'number' && Number.isSafeInteger(step) && step >= 1)) return reject(state, 'INVALID_INCREMENT');
      const previousStep = chosenStepOf(state);
      // The step already standing is not a new fact (a double tap).
      if (step === previousStep) return reject(state, 'ILLEGAL_TRANSITION');
      const lotId = state.currentLotId;
      // With a bid on the board the ask moves to the new ladder at once, from
      // the standing bid. With none, the opening ask stands (that is SET_ASK's).
      // Before the lot opens the board is not its own yet: no ask either way.
      const high = bidding ? state.currentHighBid : null;
      const ask = high === null ? state.currentAsk : step !== null ? nextAskByStep(high, step) : nextAsk(high, getIncrementTable(state.incrementTableId));
      // ADR-60: a held internet bid was at the old ask; it resolves ASK_CHANGED
      // before the ask moves, exactly as SET_ASK does.
      const pr = ask !== state.currentAsk ? resolvePending(state, 'ASK_CHANGED', now, actor.id) : { state, events: [] as DomainEvent[] };
      const iseq = pr.state.nextSeq;
      return acceptThenSettle(
        { ...pr.state, currentAsk: ask, chosenIncrement: step === null ? null : { lotId, step }, ...bump(iseq) },
        [
          ...pr.events,
          {
            type: 'INCREMENT_CHANGED',
            lotId,
            step,
            previousStep,
            ask: bidding ? ask : null,
            previousAsk: bidding ? state.currentAsk : null,
            seq: iseq,
            channel: 'public',
            actorId: actor.id,
            serverTs: now,
          },
        ],
        lotId,
        now,
      );
    }

    case 'REMOVE_FROM_RING': {
      // ADR-70 cut 6d (hitlist 14): the lot leaves the ring with no result (a
      // vet call, a horse that will not come in) and stays in the sale to be
      // offered later, its maxes and announcements kept. A lot holding a bid
      // is sold, passed or no-saled instead: nobody's bid is stranded.
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      if (state.currentLotId === null || command.lotId !== state.currentLotId) return reject(state, 'LOT_NOT_OPEN');
      if (lotHoldsBid(state)) return reject(state, 'LOT_IN_PROGRESS');
      if (!BIDDING_STATES.includes(state.lotState) && state.lotState !== 'UPCOMING') return reject(state, 'ILLEGAL_TRANSITION');
      const reason = typeof command.reason === 'string' && command.reason.trim().length > 0 ? command.reason.trim() : null;
      const lotId = state.currentLotId;
      const group = state.sellingGroup;
      // A held internet bid on the lot is closed out first, visibly.
      const pr = resolvePending(state, 'LOT_CLOSED', now, actor.id);
      const rseq = pr.state.nextSeq;
      return accept(
        {
          ...pr.state,
          currentLotId: null,
          lotState: 'UPCOMING',
          closeStep: null,
          sellingGroup: null,
          equalBidAward: null,
          currentAsk: null,
          currentHighBid: null,
          currentHighBidder: null,
          currentHighBidSource: null,
          currentHighPaddle: null,
          currentHighBidSeq: null,
          lastBid: null,
          chosenIncrement: null,
          ...bump(rseq),
        },
        [
          ...pr.events,
          {
            type: 'LOT_REMOVED_FROM_RING',
            lotId,
            reason,
            ...(group !== null ? { group: { lotIds: [...group.lotIds] } } : {}),
            seq: rseq,
            channel: 'public',
            actorId: actor.id,
            serverTs: now,
          },
        ],
      );
    }

    case 'CORRECT_FLOOR_BID': {
      // Spec 8.10. Replaces "UNDO", which was a misleading name for an
      // append-only log: nothing disappears, a compensating event is added.
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');

      const last = state.lastBid;

      // Reconciliation is required whenever the correction would rewrite a
      // chain rather than peel off its tip. The clerk must not be able to
      // casually erase a mistaken floor bid that online bidders have already
      // bid on top of - that changes who is winning and at what price.
      if (last === null) return reject(state, 'CORRECTION_REQUIRES_RECONCILIATION');
      if (last.seq !== command.reversesSeq) return reject(state, 'CORRECTION_REQUIRES_RECONCILIATION');

      // Once hammered, the provisional winner is public and possibly announced.
      // Backing a bid out from under that is a disposition decision, not a
      // clerical fix: the clerk reopens the lot first.
      if (!BIDDING_STATES.includes(state.lotState)) {
        return reject(state, 'CORRECTION_REQUIRES_RECONCILIATION');
      }

      // This command corrects clerk entry error. An online bidder's own bid is
      // withdrawn through the retraction workflow, which is a different thing
      // with different evidence requirements. (A PROXY bid is an online bid.)
      if (last.source !== 'FLOOR') return reject(state, 'ILLEGAL_TRANSITION');

      // ADR-60: the correction restores the pre-bid ask; a pending internet bid
      // at the current ask is resolved ASK_CHANGED first.
      const pr = resolvePending(state, 'ASK_CHANGED', now, actor.id);
      const cseq = pr.state.nextSeq;
      return acceptThenSettle(
        {
          ...pr.state,
          currentHighBid: last.previousHighBid,
          currentHighBidder: last.previousHighBidder,
          currentHighBidSource: last.previousHighBidSource,
          currentHighPaddle: last.previousHighPaddle,
          currentHighBidSeq: last.previousHighBidSeq,
          currentAsk: last.previousAsk,
          // A correction is not itself reversible: correcting twice in a row
          // without an intervening bid would need a second undo record, and
          // that path is reconciliation by definition.
          lastBid: null,
          // ADR-70 cut 2: correcting an award's floor bid ends the award.
          equalBidAward: null,
          ...bump(cseq),
        },
        [
          ...pr.events,
          {
            type: 'FLOOR_BID_CORRECTED',
            lotId: state.currentLotId ?? '',
            reversesSeq: command.reversesSeq,
            reason: command.reason,
            restoredHighBid: last.previousHighBid,
            restoredAsk: last.previousAsk,
            seq: cseq,
            channel: 'public',
            actorId: actor.id,
            serverTs: now,
          },
        ],
        state.currentLotId,
        now,
      );
    }

    // -----------------------------------------------------------------------
    // Equal bids - ADR-70 cut 2 (brief section 12)
    // -----------------------------------------------------------------------
    case 'AWARD_EQUAL_BID_ONSITE': {
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      const policy = state.policy.equalBid.policy;
      if (policy !== 'AUCTIONEER_AUTHORITY' && policy !== 'ONSITE_PRIORITY') return reject(state, 'EQUAL_BID_POLICY');
      if (!BIDDING_STATES.includes(state.lotState)) return reject(state, 'LOT_NOT_OPEN');
      // An override is a recorded decision about real money: say why.
      const reason = typeof command.reason === 'string' ? command.reason.trim() : '';
      if (reason.length === 0) return reject(state, 'REASON_REQUIRED');
      if (state.currentHighBid === null || state.currentHighBidder === null || state.currentHighBidSeq === null) return reject(state, 'NO_EQUAL_BID');
      if (state.currentHighBidSource === 'FLOOR') return reject(state, 'NO_EQUAL_BID');
      // The screen's bid, or nothing: awarding a bid the clerk never saw is
      // the mistake the version pin exists for.
      if (command.bidSeq !== state.currentHighBidSeq) return reject(state, 'STALE_AUCTION_STATE');
      const t = takeEqualBid(state, { to: 'ONSITE', cause: 'OPERATOR', winnerId: actor.id, winnerPaddle: paddleOrNull(command.paddleNumber), reason, actorId: actor.id, now });
      // A max behind the displaced bid answers at once, like any floor bid.
      return acceptThenSettle(t.state, t.events, state.currentLotId, now);
    }

    case 'RESTORE_EQUAL_BID_ONLINE': {
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      if (state.policy.equalBid.policy !== 'AUCTIONEER_AUTHORITY') return reject(state, 'EQUAL_BID_POLICY');
      if (!BIDDING_STATES.includes(state.lotState)) return reject(state, 'LOT_NOT_OPEN');
      const reason = typeof command.reason === 'string' ? command.reason.trim() : '';
      if (reason.length === 0) return reject(state, 'REASON_REQUIRED');
      const award = state.equalBidAward;
      if (award === null || award.lotId !== state.currentLotId) return reject(state, 'NO_EQUAL_BID');
      // Only the award the screen showed, and only while its floor bid
      // still holds the board: anything bid since is a newer decision.
      if (command.overrideSeq !== award.overrideSeq || state.currentHighBidSeq !== award.floorBidSeq) return reject(state, 'STALE_AUCTION_STATE');
      const auth = state.bidders[award.displacedBidderId];
      if (!auth || auth.approvalStatus !== 'APPROVED') return reject(state, 'NOT_APPROVED');
      if (auth.bidLimit !== undefined && state.currentHighBid !== null && state.currentHighBid > auth.bidLimit) return reject(state, 'BID_LIMIT_EXCEEDED');
      let s = state;
      const events: DomainEvent[] = [];
      // A held internet bid from the bidder being restored would now beat
      // their own standing bid: closed out first, visibly.
      if (s.pendingBid !== null && s.pendingBid.bidderId === award.displacedBidderId) {
        const pr = resolvePending(s, 'REJECTED', now, actor.id, 'equal bid restored: this bidder holds the bid');
        s = pr.state;
        events.push(...pr.events);
      }
      const oseq = s.nextSeq;
      const floorId = s.currentHighBidder!;
      events.push({
        type: 'EQUAL_BID_OVERRIDDEN',
        lotId: award.lotId,
        amount: s.currentHighBid!,
        awardedTo: 'ONLINE',
        cause: 'OPERATOR',
        policy: s.policy.equalBid.policy,
        displacedBidSeq: award.floorBidSeq,
        displacedBidSource: 'FLOOR',
        displacedBidderRef: bidderRef(floorId, 'FLOOR'),
        displacedBidderId: floorId,
        displacedPaddle: s.currentHighPaddle,
        winnerBidSeq: award.displacedBidSeq,
        winnerBidSource: award.displacedBidSource,
        winnerBidderRef: bidderRef(award.displacedBidderId, award.displacedBidSource),
        winnerBidderId: award.displacedBidderId,
        winnerPaddle: null,
        restoresSeq: award.overrideSeq,
        reason,
        seq: oseq,
        channel: 'public',
        actorId: actor.id,
        serverTs: now,
      });
      return acceptThenSettle(
        {
          ...s,
          currentHighBidder: award.displacedBidderId,
          currentHighBidSource: award.displacedBidSource,
          currentHighPaddle: null,
          // The standing bid is the online bid itself again: SOLD carries it
          // as the winning bid, the provenance the log already holds.
          currentHighBidSeq: award.displacedBidSeq,
          // An online bid is not the clerk's to correct; nothing to undo.
          lastBid: null,
          equalBidAward: null,
          ...bump(oseq),
        },
        events,
        award.lotId,
        now,
      );
    }

    // -----------------------------------------------------------------------
    // TIMED lifecycle - ADR-42
    // -----------------------------------------------------------------------
    case 'OPEN_TIMED_LOT': {
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      // Re-opening a closed timed lot is a correction workflow, not a command,
      // except a lot whose sale was VOIDED (ADR-65), which gets a second life
      // with a fresh schedule and a new openedSeq.
      const existing = state.lots[command.lotId];
      if (existing && existing.phase !== 'VOIDED') return reject(state, 'ILLEGAL_TRANSITION');
      if (standingSale(state, [command.lotId])) return reject(state, 'LOT_ALREADY_SOLD');
      if (!Number.isFinite(command.openingAsk) || command.openingAsk <= 0) return reject(state, 'INVALID_INCREMENT');
      if (!Number.isFinite(command.closesAt) || command.closesAt <= now) return reject(state, 'ILLEGAL_TRANSITION');
      const lot: TimedLot = {
        lotId: command.lotId,
        phase: 'OPEN',
        openedSeq: seq,
        originalCloseAt: command.closesAt,
        scheduledCloseAt: command.closesAt,
        extensions: 0,
        pricingMode: command.pricingMode,
        reserve: command.reserve ?? null,
        closeGroupId: command.closeGroupId ?? null,
        closeSeq: null,
        currentHighBid: null,
        currentHighBidder: null,
        currentHighBidSource: null,
        currentHighPaddle: null,
        currentHighBidSeq: null,
        currentAsk: command.openingAsk,
        lastBid: null,
      };
      const ev: DomainEvent = {
        type: 'TIMED_LOT_OPENED',
        lotId: command.lotId,
        openingAsk: command.openingAsk,
        closesAt: command.closesAt,
        pricingMode: command.pricingMode,
        closeGroupId: lot.closeGroupId,
        incrementTableId: state.incrementTableId,
        seq,
        channel: 'public',
        actorId: actor.id,
        serverTs: now,
      };
      const opened = withLot({ ...state, ...bump(seq) }, lot);
      // Maxes placed before the lot opened run now from the opening ask (unless
      // policy keeps them dormant until the first live bid).
      if (state.policy.prebid.carryIn === 'NONE') return accept(opened, [ev]);
      return acceptThenSettle(opened, [ev], command.lotId, now);
    }

    case 'REOPEN_TIMED_LOT': {
      // ADR-70 cut 6i (hitlist 17.4): the office's timed recovery, sent by the
      // control plane alone (CONTROL_PLANE_PERMISSIONS).
      if (actor.role !== 'clerk' || actor.id !== CONTROL_PLANE_ACTOR_ID) return reject(state, 'NOT_AUTHORIZED');
      const lot = state.lots[command.lotId];
      const refusal = timedReopenRefusal(state, command.lotId, command.closeSeq);
      if (refusal !== null || !lot) return reject(state, refusal ?? 'ILLEGAL_TRANSITION');
      const reason = typeof command.reason === 'string' ? command.reason.trim() : '';
      if (reason.length === 0) return reject(state, 'REASON_REQUIRED');
      if (!Number.isFinite(command.closesAt) || command.closesAt <= now) return reject(state, 'ILLEGAL_TRANSITION');
      // The board comes back as it left; the close is fresh, and so is the
      // allowance of extensions it may earn.
      const reopened: TimedLot = {
        ...lot,
        phase: 'OPEN',
        originalCloseAt: command.closesAt,
        scheduledCloseAt: command.closesAt,
        extensions: 0,
        closeGroupId: command.closeGroupId ?? null,
        closeSeq: null,
      };
      const ev: DomainEvent = {
        type: 'TIMED_LOT_REOPENED',
        lotId: command.lotId,
        closesAt: command.closesAt,
        closeGroupId: reopened.closeGroupId,
        reopensCloseSeq: command.closeSeq,
        previousCloseAt: lot.scheduledCloseAt,
        from: 'NO_SALE',
        restoredHighBid: lot.currentHighBid,
        restoredAsk: lot.currentAsk,
        restoredBidSource: lot.currentHighBidSource,
        restoredBidderRef: lot.currentHighBidder !== null ? bidderRef(lot.currentHighBidder, lot.currentHighBidSource ?? 'ONLINE') : null,
        recoveryId: command.recoveryId,
        reason,
        requestedBy: command.requestedBy,
        seq,
        channel: 'public',
        actorId: actor.id,
        serverTs: now,
      };
      // "Last result" is not about this lot any more: it is open again.
      const previousLot = state.previousLot !== null && state.previousLot.lotId === command.lotId ? null : state.previousLot;
      // Maxes that still stand on the lot run again from the board as it is.
      return acceptThenSettle(withLot({ ...state, previousLot, ...bump(seq) }, reopened), [ev], command.lotId, now);
    }

    // -----------------------------------------------------------------------
    // Online participation
    // -----------------------------------------------------------------------
    case 'PAUSE_ONLINE': {
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      if (state.onlineStatus === 'PAUSED') return accept(state, []);
      return accept({ ...state, onlineStatus: 'PAUSED', pauseReason: 'CLERK', ...bump(seq) }, [
        { type: 'ONLINE_PAUSED', reason: 'CLERK', seq, channel: 'public', actorId: actor.id, serverTs: now },
      ]);
    }

    case 'RESUME_ONLINE': {
      // Spec 8.11: always explicit, clerk only. The engine never auto-resumes -
      // a reconnecting console must re-synchronize with the physical ring
      // before internet bids rejoin an auction that has moved on.
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      if (state.onlineStatus === 'OPEN') return accept(state, []);
      // Proxies are online bids: any that became eligible while paused run now -
      // on the ring lot (LIVE) or on every open timed lot (TIMED).
      let s: RingState = { ...state, onlineStatus: 'OPEN', pauseReason: null, floorInputHealthyAt: now, ...bump(seq) };
      const events: DomainEvent[] = [{ type: 'ONLINE_RESUMED', seq, channel: 'public', actorId: actor.id, serverTs: now }];
      const lotIds = timed
        ? Object.values(s.lots).filter((l) => TIMED_BIDDING_PHASES.includes(l.phase)).map((l) => l.lotId).sort()
        : state.currentLotId !== null ? [state.currentLotId] : [];
      for (const lotId of lotIds) {
        const r = settleProxies(s, lotId, now);
        s = r.state;
        events.push(...r.events);
      }
      return accept(s, events);
    }

    case 'SET_BIDDER_APPROVAL': {
      // The control plane (clerk posture over the bridge). ADR-60 cut 4 closed
      // the hole where any floor socket could approve a bidder: a spotter's
      // FLOOR_COMMAND carrying this is refused by name.
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      const existing = state.bidders[command.bidderId];
      // Stale control-plane messages must not resurrect a revoked bidder.
      if (existing && existing.approvalVersion >= command.approvalVersion) {
        return accept(state, []);
      }
      const auth = {
        approvalStatus: command.approvalStatus,
        approvalVersion: command.approvalVersion,
        ...(command.bidLimit !== undefined ? { bidLimit: command.bidLimit } : {}),
        ...(existing?.activeSessionId !== undefined ? { activeSessionId: existing.activeSessionId } : {}),
      };
      const next: RingState = { ...state, bidders: { ...state.bidders, [command.bidderId]: auth }, ...bump(seq) };
      const ev: DomainEvent = {
        type: 'BIDDER_APPROVAL_CHANGED',
        bidderId: command.bidderId,
        approvalStatus: command.approvalStatus,
        approvalVersion: command.approvalVersion,
        seq,
        channel: 'private',
        actorId: actor.id,
        serverTs: now,
      };
      // A bidder who is no longer APPROVED has no standing to keep bidding:
      // every active max they hold is invalidated, each as its own event.
      // Bids already accepted stand. Re-approval does not resurrect a max -
      // the bidder places it again.
      if (command.approvalStatus !== 'APPROVED') {
        // ADR-60: their pending internet bid (if any) can no longer be accepted.
        let s = next;
        const evs: DomainEvent[] = [ev];
        if (s.pendingBid !== null && s.pendingBid.bidderId === command.bidderId) {
          const pr = resolvePending(s, 'REJECTED', now, actor.id);
          s = pr.state;
          evs.push(...pr.events);
        }
        const inv = invalidateMaxes(s, (m) => m.bidderId === command.bidderId, `BIDDER_${command.approvalStatus}`, false, now, actor.id);
        return accept(inv.state, [...evs, ...inv.events]);
      }
      return accept(next, [ev]);
    }

    case 'SET_RING_STAFF': {
      // ADR-70 cut 5f: the control plane alone (CONTROL_PLANE_PERMISSIONS).
      // The socket layer already refuses it from every role; this is the
      // authority behind it.
      if (actor.role !== 'clerk' || actor.id !== CONTROL_PLANE_ACTOR_ID) return reject(state, 'NOT_AUTHORIZED');
      // The roster travels whole at a version: an older or repeated one is
      // an accepted no-op, so redelivery and reordering change nothing.
      if (state.ringStaff !== null && state.ringStaff.version >= command.version) return accept(state, []);
      const ringStaff = { version: command.version, assignments: command.assignments };
      return accept({ ...state, ringStaff, ...bump(seq) }, [{
        type: 'RING_STAFF_CHANGED',
        version: command.version,
        assignments: command.assignments,
        seq,
        channel: 'private',
        actorId: actor.id,
        serverTs: now,
      }]);
    }

    case 'SET_RING_CAPABILITIES': {
      // ADR-70 cut 6e (hitlist 9.2): the Super Admin's switch, carried by
      // the control plane alone, exactly as the roster is. A tenant's own
      // clerk can never turn max-bid visibility on from a console.
      if (actor.role !== 'clerk' || actor.id !== CONTROL_PLANE_ACTOR_ID) return reject(state, 'NOT_AUTHORIZED');
      if (!Number.isInteger(command.version) || command.version < 0 || typeof command.operatorMaxBidVisibility !== 'boolean') return reject(state, 'ILLEGAL_TRANSITION');
      // ADR-70 cut 6f: operator intent signals ride the same message. A 6e
      // control plane sends neither field (intent stays off); anything sent
      // must be whole.
      if (command.operatorIntentSignals !== undefined && typeof command.operatorIntentSignals !== 'boolean') return reject(state, 'ILLEGAL_TRANSITION');
      if (command.intentControls !== undefined && !isIntentControls(command.intentControls)) return reject(state, 'ILLEGAL_TRANSITION');
      // Versioned like the roster: an older or repeated message is an
      // accepted no-op, so redelivery and reordering change nothing.
      if (state.capabilities !== null && state.capabilities.version >= command.version) return accept(state, []);
      const intent = {
        ...(command.operatorIntentSignals !== undefined ? { operatorIntentSignals: command.operatorIntentSignals } : {}),
        ...(command.intentControls !== undefined ? { intentControls: { ...command.intentControls } } : {}),
      };
      const capabilities = { version: command.version, operatorMaxBidVisibility: command.operatorMaxBidVisibility, ...intent };
      return accept({ ...state, capabilities, ...bump(seq) }, [{
        type: 'RING_CAPABILITIES_CHANGED',
        version: command.version,
        operatorMaxBidVisibility: command.operatorMaxBidVisibility,
        ...intent,
        seq,
        channel: 'private',
        actorId: actor.id,
        serverTs: now,
      }]);
    }

    case 'VIEW_MAX_BIDS': {
      // ADR-70 cut 6e (hitlist 9.2): the clerk's console looks at the maxes
      // on a lot. Off by default; a spotter, a bidder or an observer never.
      // The control plane carries switches, it does not look.
      if (actor.role !== 'clerk' || actor.id === CONTROL_PLANE_ACTOR_ID) return reject(state, 'NOT_AUTHORIZED');
      if (state.capabilities === null || !state.capabilities.operatorMaxBidVisibility) return reject(state, 'CAPABILITY_OFF');
      if (typeof command.lotId !== 'string' || command.lotId.length === 0) return reject(state, 'ILLEGAL_TRANSITION');
      // The staff role the ring server proved at sign-in, stamped over
      // whatever the console sent; a command without one is a clerk's view,
      // the narrower of the two.
      const viewerRole: MaxBidViewerRole = command.viewerRole === 'admin' ? 'admin' : 'clerk';
      const scopes = MAX_BID_VIEW_SCOPES[viewerRole];
      const sees = (scope: (typeof scopes)[number]): boolean => scopes.includes(scope);
      // What is left of a max is measured against the lot's own high bid:
      // the TIMED lot's, or the LIVE ring's while the lot is in it.
      const high = timed ? (state.lots[command.lotId]?.currentHighBid ?? null) : state.currentLotId === command.lotId ? state.currentHighBid : null;
      const active = Object.values(state.maxBids[command.lotId] ?? {})
        .filter((m) => m.status === 'ACTIVE')
        .sort((a, b) => a.placedSeq - b.placedSeq);
      // Only what this viewer may see goes into the record: the record is
      // what was shown. placedSeq names the MAX_BID_PLACED behind each one,
      // so the full max is always one lookup away in the log.
      const viewed: ViewedMaxBid[] = active.map((m) => ({
        placedSeq: m.placedSeq,
        ...(sees('view_proxy_bidder') ? { bidderId: m.bidderId } : {}),
        ...(sees('view_proxy_amount') ? { maxAmount: m.maxAmount } : {}),
        ...(sees('view_proxy_remaining_capacity') ? { remaining: high === null ? null : Math.max(0, m.maxAmount - high) } : {}),
      }));
      // Every view is recorded, an empty one too: the question was asked.
      return accept({ ...state, ...bump(seq) }, [{
        type: 'MAX_BID_VIEWED',
        lotId: command.lotId,
        viewerRole,
        scopes,
        activeCount: active.length,
        viewed,
        seq,
        channel: 'private',
        actorId: actor.id,
        serverTs: now,
      }]);
    }

    // -----------------------------------------------------------------------
    // Material announcements - ADR-42
    // -----------------------------------------------------------------------
    case 'POST_ANNOUNCEMENT': {
      // Legally significant at a horse sale: clerk only, any lot state (a
      // disclosure after SOLD is still a fact worth recording), never silent.
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      if (typeof command.text !== 'string' || command.text.trim().length === 0) return reject(state, 'ILLEGAL_TRANSITION');
      // What happens to standing maxes: configured policy, MATERIAL only. A
      // sale-wide MATERIAL announcement (lotId null) applies to every lot.
      const applied = command.severity === 'MATERIAL' ? state.policy.announcements.onMaterial : 'NONE';
      // ADR-60 cut 3: does every bidder have to read this before bidding again?
      const requiresAck = command.requiresAcknowledgement ?? (command.severity === 'MATERIAL' && state.policy.announcements.requireAckOnMaterial);
      const ev: DomainEvent = {
        type: 'ANNOUNCEMENT_POSTED',
        lotId: command.lotId,
        text: command.text,
        severity: command.severity,
        category: command.category,
        supersedesSeq: command.supersedesSeq ?? null,
        maxBidPolicyApplied: applied,
        requiresAcknowledgement: requiresAck,
        seq,
        channel: 'public',
        actorId: actor.id,
        serverTs: now,
      };
      let posted: RingState = { ...state, ...bump(seq) };
      const evs: DomainEvent[] = [ev];
      if (requiresAck) {
        const scope = command.lotId ?? SALE_WIDE_SCOPE;
        const req: RequiredAcknowledgement = { seq, lotId: command.lotId, text: command.text, category: command.category, severity: command.severity };
        posted = { ...posted, requiredAcks: { ...posted.requiredAcks, [scope]: [...(posted.requiredAcks[scope] ?? []), req].slice(-MAX_REQUIRED_ACKS_PER_SCOPE) } };
        // A bid waiting for the operator was placed on the lot as it WAS.
        if (posted.pendingBid !== null && (command.lotId === null || posted.pendingBid.lotId === command.lotId)) {
          const pr = resolvePending(posted, 'LOT_CHANGED', now, actor.id);
          posted = pr.state;
          evs.push(...pr.events);
        }
      }
      if (applied === 'NONE' || applied === 'PRESERVE_MAX_BIDS') return accept(posted, evs);
      // INVALIDATE_MAX_BIDS: each max is ended by its own event. REQUIRE_RECONFIRM:
      // the same, flagged so the bidder's client asks them to re-place (re-placing
      // IS the reconfirmation - it carries fresh intent and fresh priority).
      const inv = invalidateMaxes(
        posted,
        (m) => command.lotId === null || m.lotId === command.lotId,
        `MATERIAL_ANNOUNCEMENT:${seq}`,
        applied === 'REQUIRE_RECONFIRM',
        now,
        actor.id,
      );
      return accept(inv.state, [...evs, ...inv.events]);
    }

    case 'ACKNOWLEDGE_ANNOUNCEMENTS': {
      if (actor.role !== 'bidder') return reject(state, 'NOT_AUTHORIZED');
      const scope = command.lotId ?? SALE_WIDE_SCOPE;
      const required = state.requiredAcks[scope] ?? [];
      // Acknowledging nothing that exists, or a seq beyond what was posted, is
      // refused: an acknowledgement names what was read.
      if (!Number.isFinite(command.throughSeq) || command.throughSeq <= 0) return reject(state, 'ILLEGAL_TRANSITION');
      if (!required.some((r) => r.seq <= command.throughSeq)) return reject(state, 'ILLEGAL_TRANSITION');
      if (command.throughSeq > state.lastSeq) return reject(state, 'ILLEGAL_TRANSITION');
      const mine = state.acknowledgements[actor.id] ?? {};
      if ((mine[scope] ?? 0) >= command.throughSeq) return accept(state, []);
      const next: RingState = {
        ...state,
        acknowledgements: { ...state.acknowledgements, [actor.id]: { ...mine, [scope]: command.throughSeq } },
        ...bump(seq),
      };
      return accept(next, [
        { type: 'ANNOUNCEMENT_ACKNOWLEDGED', bidderId: actor.id, lotId: command.lotId, throughSeq: command.throughSeq, sessionId: null, seq, channel: 'private', actorId: actor.id, serverTs: now },
      ]);
    }

    // -----------------------------------------------------------------------
    // Max (proxy) bids - ADR-42
    // -----------------------------------------------------------------------
    case 'PLACE_MAX_BID': {
      if (actor.role !== 'bidder') return reject(state, 'NOT_AUTHORIZED');
      if (!state.policy.proxy.enabled) return reject(state, 'PROXY_DISABLED');
      const timedLot = timed ? state.lots[command.lotId] : undefined;
      // (A max naming a member lot of a group was re-pointed at the lead above.)
      const isCurrent = !timed && lotInRing(state, command.lotId);
      if (state.policy.proxy.scope === 'CURRENT_LOT' && !isCurrent) return reject(state, 'PROXY_DISABLED');
      if (isCurrent) {
        if (POST_HAMMER_STATES.includes(state.lotState)) return reject(state, 'POST_HAMMER');
        if (state.lotState === 'PASSED' || state.lotState === 'SCRATCHED') return reject(state, 'LOT_NOT_OPEN');
      }
      // TIMED: a max on a lot that has already closed can never execute.
      if (timedLot && !timedLotBiddable(timedLot, now)) return reject(state, 'LOT_CLOSED');
      const auth = state.bidders[actor.id];
      if (!auth || auth.approvalStatus !== 'APPROVED') return reject(state, 'NOT_APPROVED');
      if (auth.activeSessionId !== undefined && auth.activeSessionId !== command.sessionId) return reject(state, 'SESSION_NOT_ACTIVE');
      // ADR-60 cut 3: a max on a lot with an unread material update is refused.
      if (unacknowledgedAnnouncements(state, actor.id, command.lotId).length > 0) return reject(state, 'ACK_REQUIRED');
      if (!Number.isFinite(command.maxAmount) || command.maxAmount <= 0) return reject(state, 'INVALID_INCREMENT');
      if (auth.bidLimit !== undefined && command.maxAmount > auth.bidLimit) return reject(state, 'BID_LIMIT_EXCEEDED');
      // A max that can never execute is refused, not stored as a dead order;
      // lowering under your own standing high bid would disown a bid you hold.
      const livePos = isCurrent && BIDDING_STATES.includes(state.lotState) ? ringPosition(state) : null;
      const pos = livePos ?? (timedLot ? timedPosition(timedLot) : null);
      if (pos) {
        if (pos.currentAsk !== null && command.maxAmount < pos.currentAsk) return reject(state, 'MAX_BELOW_ASK');
        if (pos.currentHighBidder === actor.id && pos.currentHighBid !== null && command.maxAmount < pos.currentHighBid) return reject(state, 'MAX_BELOW_ASK');
      }
      const existing = state.maxBids[command.lotId]?.[actor.id];
      const replacesSeq = existing && existing.status === 'ACTIVE' ? existing.placedSeq : null;
      const placed: MaxBid = { bidderId: actor.id, lotId: command.lotId, maxAmount: command.maxAmount, placedSeq: seq, sessionId: command.sessionId, status: 'ACTIVE' };
      const next: RingState = { ...state, maxBids: setMax(state.maxBids, command.lotId, actor.id, placed), ...bump(seq) };
      const ev: DomainEvent = { type: 'MAX_BID_PLACED', lotId: command.lotId, bidderId: actor.id, maxAmount: command.maxAmount, replacesSeq, seq, channel: 'private', actorId: actor.id, serverTs: now };
      // On a biddable lot the max competes immediately (if eligible).
      return acceptThenSettle(next, [ev], isCurrent || timedLot ? command.lotId : null, now);
    }

    case 'CANCEL_MAX_BID': {
      if (actor.role !== 'bidder') return reject(state, 'NOT_AUTHORIZED');
      const existing = state.maxBids[command.lotId]?.[actor.id];
      if (!existing || existing.status !== 'ACTIVE') return reject(state, 'NO_ACTIVE_MAX');
      return accept(
        { ...state, maxBids: setMax(state.maxBids, command.lotId, actor.id, { ...existing, status: 'CANCELLED', endedReason: 'CANCELLED' }), ...bump(seq) },
        [{ type: 'MAX_BID_CANCELLED', lotId: command.lotId, bidderId: actor.id, maxSeq: existing.placedSeq, seq, channel: 'private', actorId: actor.id, serverTs: now }],
      );
    }

    case 'INVALIDATE_LOT_MAX_BIDS': {
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      const inv = invalidateMaxes(state, (m) => m.lotId === command.lotId, `CLERK:${command.reason}`, false, now, actor.id);
      return accept(inv.state, inv.events);
    }

    case 'CONTINUE_PROXY_RESOLUTION': {
      // Engine-issued only (system actor, clerk posture like TICK): finish a
      // proxy alternation the previous commit capped for size. Bounded by the
      // same PROXY_STEPS_PER_DECISION cap, so this commit is small too.
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      const r = settleProxies(state, command.lotId, now);
      return accept(r.state, r.events);
    }

    // -----------------------------------------------------------------------
    // Operator-accepted online bids - ADR-60 cut 2
    // -----------------------------------------------------------------------
    case 'ACCEPT_ONLINE_BID': {
      if (!isFloorInput(actor.role)) return reject(state, 'NOT_AUTHORIZED');
      const p = state.pendingBid;
      if (p === null || p.id !== command.pendingBidId) return reject(state, 'NO_PENDING_BID');
      if (p.lotId !== state.currentLotId || !BIDDING_STATES.includes(state.lotState)) return reject(state, 'LOT_NOT_OPEN');
      // Cannot happen (every ask move resolves the pending bid first), but the
      // invariant is checked rather than assumed.
      if (state.currentAsk !== p.amount) return reject(state, 'ASK_CHANGED');
      const auth = state.bidders[p.bidderId];
      if (!auth || auth.approvalStatus !== 'APPROVED') return reject(state, 'NOT_APPROVED');
      // The accepted bid is an ORDINARY bid through the shared bid core, so
      // every invariant (high <= ask, ladder-aligned, provenance) holds by
      // construction; the resolution precedes it in the log.
      const pr = resolvePending(state, 'ACCEPTED', now, actor.id);
      const bseq = pr.state.nextSeq;
      const r = acceptBidIntoPosition(ringPosition(pr.state), pr.state.incrementTableId, {
        lotId: p.lotId, bidderId: p.bidderId, amount: p.amount, source: p.bidSource, paddleNumber: null, seq: bseq, now, lotState: pr.state.lotState,
        ...(p.proxyOfMaxSeq !== null ? { proxyOfMaxSeq: p.proxyOfMaxSeq } : {}),
      }, chosenStepOf(pr.state));
      const committed = withRingPosition({ ...pr.state, ...bump(bseq) }, r.pos);
      return acceptThenSettle(committed, [...pr.events, r.event], p.lotId, now);
    }

    case 'DECLINE_ONLINE_BID': {
      if (!isFloorInput(actor.role)) return reject(state, 'NOT_AUTHORIZED');
      const p = state.pendingBid;
      if (p === null || p.id !== command.pendingBidId) return reject(state, 'NO_PENDING_BID');
      const pr = resolvePending(state, 'REJECTED', now, actor.id, command.reason?.trim() || null);
      let s = pr.state;
      const events: DomainEvent[] = [...pr.events];
      // A declined proxy step would only pend again from the same max: end
      // that max, visibly, so the operator's decision sticks.
      if (p.proxyOfMaxSeq !== null) {
        const m = s.maxBids[p.lotId]?.[p.bidderId];
        if (m && m.status === 'ACTIVE' && m.placedSeq === p.proxyOfMaxSeq) {
          const xseq = s.nextSeq;
          events.push({ type: 'PROXY_EXHAUSTED', lotId: p.lotId, bidderId: p.bidderId, maxSeq: m.placedSeq, atAsk: p.amount, reason: 'DECLINED', seq: xseq, channel: 'private', actorId: actor.id, serverTs: now });
          s = { ...s, maxBids: setMax(s.maxBids, p.lotId, p.bidderId, { ...m, status: 'EXHAUSTED', endedReason: 'DECLINED' }), ...bump(xseq) };
        }
      }
      // Other maxes had been waiting behind the declined bid.
      return acceptThenSettle(s, events, p.lotId, now);
    }

    case 'SET_CONFIRM_MODE': {
      if (actor.role !== 'clerk') return reject(state, 'NOT_AUTHORIZED');
      // Never mid-lot: the acceptance rule for a lot is fixed while it is live.
      if (state.lotState === 'OPEN' || state.lotState === 'FAIR_WARNING' || state.lotState === 'HAMMERED_PENDING') return reject(state, 'ILLEGAL_TRANSITION');
      if (state.confirmMode === command.enabled) return accept(state, []);
      // Once the sale has started, a change is a recorded operational decision.
      const reason = command.reason?.trim() ?? '';
      if (state.currentLotId !== null && reason.length === 0) return reject(state, 'REASON_REQUIRED');
      return accept({ ...state, confirmMode: command.enabled, ...bump(seq) }, [
        { type: 'CONFIRM_MODE_SET', enabled: command.enabled, reason: reason.length > 0 ? reason : null, seq, channel: 'public', actorId: actor.id, serverTs: now },
      ]);
    }

    // -----------------------------------------------------------------------
    // Bidder session fencing - ADR-60 cut 2 (spec 8.14)
    // -----------------------------------------------------------------------
    case 'CLAIM_BIDDING_SESSION': {
      if (actor.role !== 'bidder') return reject(state, 'NOT_AUTHORIZED');
      if (state.policy.sessions.policy === 'MULTIPLE_ACTIVE_BIDDING_DEVICES') return accept(state, []);
      const existing = state.bidders[actor.id];
      if (existing?.activeSessionId === command.sessionId) return accept(state, []);
      // A bidder may claim before their approval arrives (they connect early);
      // the entry is PENDING at version 0 and the bridge's approval overrides it.
      const auth: BidderAuthorization = { ...(existing ?? { approvalStatus: 'PENDING', approvalVersion: 0 }), activeSessionId: command.sessionId };
      return accept({ ...state, bidders: { ...state.bidders, [actor.id]: auth }, ...bump(seq) }, [
        { type: 'BIDDING_SESSION_TRANSFERRED', bidderId: actor.id, sessionId: command.sessionId, previousSessionId: existing?.activeSessionId ?? null, seq, channel: 'private', actorId: actor.id, serverTs: now },
      ]);
    }

    // -----------------------------------------------------------------------
    // Bidding
    // -----------------------------------------------------------------------
    case 'PLACE_BID':
    case 'PLACE_FLOOR_BID': {
      const source: BidSource = command.type === 'PLACE_BID' ? 'ONLINE' : 'FLOOR';

      if (source === 'FLOOR' && !isFloorInput(actor.role)) return reject(state, 'NOT_AUTHORIZED');
      if (source === 'ONLINE' && actor.role !== 'bidder') return reject(state, 'NOT_AUTHORIZED');

      // ---- TIMED: bid on a per-lot position against an authoritative close ----
      if (timed && command.type === 'PLACE_BID') {
        const lot = state.lots[command.lotId];
        if (!lot) return reject(state, 'LOT_NOT_OPEN');
        // The close is decided by engine now vs the committed scheduledCloseAt -
        // whether or not a TICK has materialized it yet. "I clicked before zero"
        // is answered by this line and the attempt record, never by a browser clock.
        if (!timedLotBiddable(lot, now)) return reject(state, 'LOT_CLOSED');
        if (state.onlineStatus === 'PAUSED') return reject(state, 'ONLINE_PAUSED');
        const auth = state.bidders[actor.id];
        if (!auth || auth.approvalStatus !== 'APPROVED') return reject(state, 'NOT_APPROVED');
        if (auth.activeSessionId !== undefined && auth.activeSessionId !== command.sessionId) return reject(state, 'SESSION_NOT_ACTIVE');
        if (auth.bidLimit !== undefined && command.atAsk > auth.bidLimit) return reject(state, 'BID_LIMIT_EXCEEDED');
        if (unacknowledgedAnnouncements(state, actor.id, command.lotId).length > 0) return reject(state, 'ACK_REQUIRED');
        if (lot.currentAsk === null) return reject(state, 'LOT_NOT_OPEN');
        if (command.atAsk !== lot.currentAsk) {
          const beaten = lot.currentHighBid === command.atAsk && lot.currentHighBidder !== null && lot.currentHighBidder !== actor.id;
          return reject(state, beaten ? 'OUTBID_IN_FLIGHT' : 'ASK_CHANGED');
        }
        if (lot.currentHighBid !== null && command.atAsk <= lot.currentHighBid) return reject(state, 'BELOW_HIGH_BID');
        const r = acceptBidIntoPosition(timedPosition(lot), state.incrementTableId, {
          lotId: command.lotId, bidderId: actor.id, amount: command.atAsk, source, paddleNumber: null, seq, now, lotState: 'OPEN',
        });
        let s = withLot({ ...state, ...bump(seq) }, { ...lot, ...r.pos });
        const events: DomainEvent[] = [r.event];
        // Soft close: a bid inside the window moves the close (and its group).
        const ext = applyExtension(s, command.lotId, seq, now);
        s = ext.state;
        events.push(...ext.events);
        // Standing maxes answer in the same commit (each proxy bid may extend too).
        return acceptThenSettle(s, events, command.lotId, now);
      }

      // ---- LIVE ----
      // A floor bid is also proof of floor-input liveness.
      const base: RingState = source === 'FLOOR' ? { ...state, floorInputHealthyAt: now } : state;

      // Ordering of these checks is deliberate: it determines which reason the
      // bidder is told, and POST_HAMMER in particular is dispute evidence.
      if (POST_HAMMER_STATES.includes(base.lotState)) return reject(base, 'POST_HAMMER');
      // (A bid naming a member lot of a group was re-pointed at the lead above.)
      if (!lotInRing(base, command.lotId)) return reject(base, 'LOT_NOT_OPEN');
      if (!BIDDING_STATES.includes(base.lotState)) return reject(base, 'LOT_NOT_OPEN');
      if (source === 'ONLINE' && base.onlineStatus === 'PAUSED') return reject(base, 'ONLINE_PAUSED');

      // Narrowed on the discriminant rather than on `source`, so the compiler
      // can see that sessionId is present.
      if (command.type === 'PLACE_BID') {
        const auth = base.bidders[actor.id];
        if (!auth || auth.approvalStatus !== 'APPROVED') return reject(base, 'NOT_APPROVED');
        if (auth.activeSessionId !== undefined && auth.activeSessionId !== command.sessionId) {
          return reject(base, 'SESSION_NOT_ACTIVE');
        }
        if (auth.bidLimit !== undefined && command.atAsk > auth.bidLimit) {
          return reject(base, 'BID_LIMIT_EXCEEDED');
        }
        // ADR-60 cut 3: a material update the bidder has not read blocks the
        // bid until they acknowledge it (brief section 26).
        if (unacknowledgedAnnouncements(base, actor.id, command.lotId).length > 0) return reject(base, 'ACK_REQUIRED');
      }

      if (base.currentAsk === null) return reject(base, 'LOT_NOT_OPEN');

      // ADR-70 cut 2: an equal bid under a priority policy. The bid names the
      // amount the other side already holds, and the ring's policy, not the
      // arrival order, decides who keeps it. Judged before the confirm-mode
      // hold and before a floor bid supersedes a held internet bid: the ask
      // does not move, so a bid held at it is still good. Under any other
      // policy this falls through to OUTBID_IN_FLIGHT exactly as before.
      if (base.currentHighBid !== null && base.currentHighBidder !== null && base.currentHighBidSeq !== null && command.atAsk === base.currentHighBid) {
        const eq = base.policy.equalBid.policy;
        if (command.type === 'PLACE_FLOOR_BID' && eq === 'ONSITE_PRIORITY' && base.currentHighBidSource !== 'FLOOR') {
          const t = takeEqualBid(base, { to: 'ONSITE', cause: 'POLICY', winnerId: actor.id, winnerPaddle: command.paddleNumber ?? null, reason: 'equal bid: the ring gives the floor priority', actorId: actor.id, now });
          return acceptThenSettle(t.state, t.events, command.lotId, now);
        }
        if (command.type === 'PLACE_BID' && eq === 'ONLINE_PRIORITY' && !base.confirmMode && base.currentHighBidSource === 'FLOOR') {
          const t = takeEqualBid(base, { to: 'ONLINE', cause: 'POLICY', winnerId: actor.id, winnerPaddle: null, reason: 'equal bid: the ring gives online bidders priority', actorId: actor.id, now });
          return acceptThenSettle(t.state, t.events, command.lotId, now);
        }
      }

      // ADR-60 CONFIRM MODE: an internet bid does not move the board. It is
      // validated exactly like a live bid (the at-ask guard, the high-bid
      // guard) and then HELD as the ring's one pending bid for the operator.
      // A second internet bid while one is pending is OUTBID_IN_FLIGHT: one
      // at a time, in arrival order, is what "the operator accepts internet
      // bids" means.
      if (command.type === 'PLACE_BID' && base.confirmMode) {
        if (command.atAsk !== base.currentAsk) {
          const beaten = base.currentHighBid === command.atAsk && base.currentHighBidder !== null && base.currentHighBidder !== actor.id;
          return reject(base, beaten ? 'OUTBID_IN_FLIGHT' : 'ASK_CHANGED');
        }
        if (base.currentHighBid !== null && command.atAsk <= base.currentHighBid) return reject(base, 'BELOW_HIGH_BID');
        if (base.pendingBid !== null) return reject(base, 'OUTBID_IN_FLIGHT');
        const held = holdPending(base, { bidderId: actor.id, sessionId: command.sessionId, lotId: command.lotId, amount: command.atAsk, bidSource: 'ONLINE', proxyOfMaxSeq: null, idempotencyKey: command.idempotencyKey }, now, actor.id);
        return accept(held.state, held.events);
      }

      // ATOMIC CUT FLOOR BID (ADR-42 D1). A cut bid ("five seventy-five" when
      // the ask is six) used to be two commands - SET_ASK then PLACE_FLOOR_BID -
      // and an online bid at the new ask could legitimately commit in between,
      // taking the bid the auctioneer had just pointed at. With `cutAsk` the
      // ask move and the bid are ONE transition: ASK_SET (seq n) and
      // BID_ACCEPTED (seq n+1) in one Decision, one commit. No competing
      // command can ever observe the intermediate ask. Both events stay in the
      // log for auditability; lastBid.previousAsk is the PRE-cut ask so a
      // CORRECT_FLOOR_BID restores the board exactly as it was.
      let working = base;
      const events: DomainEvent[] = [];
      // ADR-60: a FLOOR bid supersedes a pending internet bid - the ring moved
      // on while the operator had not taken it. Resolved first, visibly.
      if (command.type === 'PLACE_FLOOR_BID' && working.pendingBid !== null) {
        const pr = resolvePending(working, 'SUPERSEDED_BY_FLOOR', now, actor.id);
        working = pr.state;
        events.push(...pr.events);
      }
      let bidSeq = working.nextSeq;
      if (command.type === 'PLACE_FLOOR_BID' && command.cutAsk === true && command.atAsk !== base.currentAsk) {
        // A cut moves the ask - clerk authority (spec 6.2), same as SET_ASK.
        if (actor.role !== 'clerk') return reject(base, 'NOT_AUTHORIZED');
        // Same guards as SET_ASK: positive, and never to/under the standing high.
        if (!Number.isFinite(command.atAsk) || command.atAsk <= 0) return reject(base, 'INVALID_INCREMENT');
        if (base.currentHighBid !== null && command.atAsk <= base.currentHighBid) return reject(base, 'BELOW_HIGH_BID');
        const askSeq = working.nextSeq;
        events.push({
          type: 'ASK_SET',
          lotId: command.lotId,
          ask: command.atAsk,
          reason: 'cut bid from the floor',
          seq: askSeq,
          channel: 'public',
          actorId: actor.id,
          serverTs: now,
        });
        working = { ...working, currentAsk: command.atAsk, ...bump(askSeq) };
        bidSeq = askSeq + 1;
      }

      // The bid-at-ask guard. If the ask moved between render and arrival, the
      // engine never silently transforms the bidder's intent (spec 20 of the
      // revisions): the bidder saw $90,000 and either gets $90,000 or a reason.
      if (command.atAsk !== working.currentAsk) {
        // Both cases are "the ask moved", but they are different facts and the
        // bidder deserves the right one. If someone else now holds the high bid
        // at exactly the amount this bidder tried to bid, they were beaten to
        // it; anything else means the clerk or the ladder moved the ask.
        const beaten =
          working.currentHighBid === command.atAsk &&
          working.currentHighBidder !== null &&
          working.currentHighBidder !== actor.id;
        return reject(base, beaten ? 'OUTBID_IN_FLIGHT' : 'ASK_CHANGED');
      }
      // Belt and braces with SET_ASK's guard: the high bid only goes up.
      if (working.currentHighBid !== null && command.atAsk <= working.currentHighBid) return reject(base, 'BELOW_HIGH_BID');

      // One atomic transition through the shared bid core: the bid and its
      // resulting ask commit together, so no client ever renders high-bid == ask
      // (revisions section 5).
      const r = acceptBidIntoPosition(ringPosition(working), working.incrementTableId, {
        lotId: command.lotId,
        bidderId: actor.id,
        amount: command.atAsk,
        source,
        paddleNumber: command.type === 'PLACE_FLOOR_BID' ? (command.paddleNumber ?? null) : null,
        seq: bidSeq,
        now,
        lotState: working.lotState,
      }, chosenStepOf(working));
      events.push(r.event);
      const committed = withRingPosition({ ...working, ...bump(bidSeq) }, {
        ...r.pos,
        // The undo record's previousAsk is the PRE-cut ask: correcting a cut bid
        // puts the board back where it was before the auctioneer took the cut.
        lastBid: r.pos.lastBid ? { ...r.pos.lastBid, previousAsk: base.currentAsk } : null,
      });
      // Any standing max on this lot answers the manual bid in the same commit.
      return acceptThenSettle(committed, events, command.lotId, now);
    }
  }
}

/**
 * Apply the floor-input rule and the timed close sweep, then the command.
 * **This is what the engine must call** - not `reduce` directly.
 *
 * `reduce` deliberately holds to one command, one outcome: a rejected command
 * never mutates the ring. But the floor-input auto-pause (spec 9.1) and the
 * authoritative timed close (ADR-42) are real state changes that must land
 * *before* a bid is judged, or a bid could slip through a blackout window no
 * timer has noticed yet - or land on a lot whose scheduled close has passed but
 * whose timer has not fired.
 *
 * Both properties are preserved by making it two commands: an implicit TICK,
 * then the real one. The pause / close get their own sequence numbers and their
 * own broadcast events, exactly as if the timer had fired.
 */
/** Commands only a person at a console sends. TICK and CONTINUE_PROXY_RESOLUTION
 *  are the engine's own; SET_BIDDER_APPROVAL can arrive from the control plane
 *  bridge, so neither proves a floor is alive. */
const FLOOR_CONSOLE_COMMANDS: ReadonlySet<Command['type']> = new Set<Command['type']>([
  'HEARTBEAT', 'SET_CURRENT_LOT', 'OPEN_LOT', 'OPEN_TIMED_LOT', 'SET_ASK', 'DECLARE_FAIR_WARNING', 'HAMMER', 'CONFIRM_SOLD', 'DECLARE_NO_SALE', 'DECLARE_PASSED',
  'REOPEN_LOT', 'CORRECT_FLOOR_BID', 'PLACE_FLOOR_BID', 'PAUSE_ONLINE', 'RESUME_ONLINE', 'POST_ANNOUNCEMENT', 'ACCEPT_ONLINE_BID', 'DECLINE_ONLINE_BID',
  'SET_CONFIRM_MODE', 'INVALIDATE_LOT_MAX_BIDS', 'FINALIZE_SALE', 'VOID_SALE', 'AWARD_EQUAL_BID_ONSITE', 'RESTORE_EQUAL_BID_ONLINE',
  'RETRACT_LAST_BID', 'RESET_BIDDING', 'SET_INCREMENT', 'REMOVE_FROM_RING', 'VIEW_MAX_BIDS',
]);

export function step(
  state: RingState,
  command: Command,
): { readonly decision: Decision; readonly state: RingState; readonly events: readonly DomainEvent[] } {
  const events: DomainEvent[] = [];
  let current = state;

  if (command.type !== 'TICK' && command.type !== 'HEARTBEAT') {
    // The implicit tick is the ENGINE's act on the command's behalf: it keeps
    // the command's actor id in the log and the clerk posture the tick needs
    // (a bidder's socket cannot drive time, but time must still be evaluated
    // before their bid is judged).
    const ticked = reduce(current, { type: 'TICK', actor: { id: command.actor.id, role: 'clerk' }, now: command.now });
    if (ticked.status === 'accepted') {
      current = ticked.state;
      events.push(...ticked.events);
    }
  }

  const decision = reduce(current, command);
  if (decision.status === 'accepted') {
    current = decision.state;
    events.push(...decision.events);
    // ADR-60 cut 9: a console command IS floor input. Found by the failover
    // drill: a clerk who opened a lot and had not heartbeated since connecting
    // (or whose first heartbeat was 400 ms away) saw online bidding auto-pause
    // on the very next tick, because only HEARTBEAT, RESUME_ONLINE and floor
    // bids refreshed liveness. Every accepted command that only a person at
    // the console sends now counts, so the pause rule measures what it means:
    // a floor that has gone silent, not a floor that is busy.
    if (isFloorInput(command.actor.role) && FLOOR_CONSOLE_COMMANDS.has(command.type)) {
      current = { ...current, floorInputHealthyAt: command.now };
    }
  }
  return { decision, state: current, events };
}

/** Replay a command sequence from an initial state. Used by the engine on
 *  failover and by the determinism property test. */
export function replay(initial: RingState, commands: readonly Command[]): RingState {
  let state = initial;
  for (const command of commands) {
    const decision = reduce(state, command);
    if (decision.status === 'accepted') state = decision.state;
  }
  return state;
}
